import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';
import { Button, ButtonGroup, Checkbox, CheckPicker, DateRangePicker, Divider, Dropdown, Loader, Message, Panel, SelectPicker, Stack } from 'rsuite';

import {
  Chart as ChartJS,
  CategoryScale,
  Decimation,
  Filler,
  Title,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import {
  useProjects,
  useTaskOrders,
  useTasks,
  useUsers,
  useCloseDates,
  useProjectForecastedHours,
  useProjectForecastedRevenue,
  useProjectActualRevenue,
  useProjectProjectedSpend,
  useProjectInvoicedAmount,
  useProjectHoursByUser,
  useProjectChartDateSpan,
  useProjectActualHours,
  useAllProjectsChartsSummary,
  useAllProjectsForecastedRevenue,
  useProjectMilestones,
  useProjectsMilestones,
  useUsersTimeOffDays,
} from '../hooks/useQueries';

import {
  bucketStartForInterval,
  buildPeriodStarts,
  formatCurrency,
  formatHours,
  formatTooltipTitle,
  formatXAxisTick,
  parseISODate,
  readCssVar,
  toISODate,
} from '../utils/chartUtils';
import { exportSheetsToXlsx } from '../utils/excelExport';
import { pickCurrentTaskOrder } from '../utils/taskOrders';

import './ChartsPage.css';

import { FilterBar, FilterField } from '@nsi-advisory-services/components';
const milestonePlugin = {
  id: 'milestoneLines',
  beforeDatasetsDraw: (chart, _args, pluginOptions) => {
    const milestones = Array.isArray(pluginOptions?.milestones) ? pluginOptions.milestones : [];
    const interval = String(pluginOptions?.interval || 'month');
    if (milestones.length === 0) return;

    const xScale = chart?.scales?.x;
    const area = chart?.chartArea;
    const labels = Array.isArray(chart?.data?.labels) ? chart.data.labels.map(String) : [];
    if (!xScale || !area || labels.length === 0) return;

    const ctx = chart.ctx;
    const stroke = 'rgba(99, 102, 241, 0.75)';
    const milestonesByIndex = new Map();

    for (const milestone of milestones) {
      const d = parseISODate(String(milestone?.milestone_date || '').slice(0, 10));
      if (!d) continue;

      const bucket = bucketStartForInterval(interval, d);
      const bucketIso = toISODate(bucket);
      if (!bucketIso) continue;

      const index = labels.findIndex((l) => l === bucketIso);
      if (index < 0) continue;

      const x = xScale.getPixelForValue(index);
      if (!Number.isFinite(x)) continue;

      const label = String(milestone?.name || 'Milestone').trim() || 'Milestone';
      if (!milestonesByIndex.has(index)) {
        milestonesByIndex.set(index, { x, labels: [] });
      }
      milestonesByIndex.get(index).labels.push(label);
    }

    for (const [, group] of milestonesByIndex.entries()) {
      const x = Number(group?.x);
      if (!Number.isFinite(x)) continue;
      const labelsForDate = Array.isArray(group?.labels) ? group.labels : [];
      if (labelsForDate.length === 0) continue;

      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(x, area.top);
      ctx.lineTo(x, area.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      const rawLabel = labelsForDate.length > 1
        ? `${labelsForDate.length} milestones`
        : String(labelsForDate[0] || 'Milestone');
      const label = (() => {
        const colonIdx = rawLabel.indexOf(':');
        if (colonIdx <= 0) return rawLabel;
        const projectPart = rawLabel.slice(0, colonIdx).trim();
        const milestonePart = rawLabel.slice(colonIdx + 1).trim();
        const shortProject = projectPart.length > 24 ? `${projectPart.slice(0, 24)}…` : projectPart;
        return milestonePart ? `${shortProject}: ${milestonePart}` : shortProject;
      })();
      ctx.font = '11px sans-serif';
      ctx.textBaseline = 'top';

      const maxLabelTextWidth = 140;
      const maxLabelLines = 2;
      const trimToWidth = (text) => {
        const s0 = String(text || '').trim();
        if (!s0) return '';
        if (ctx.measureText(s0).width <= maxLabelTextWidth) return s0;
        let s = s0;
        while (s.length > 1 && ctx.measureText(`${s}…`).width > maxLabelTextWidth) {
          s = s.slice(0, -1);
        }
        return `${s}…`;
      };
      const words = label.split(/\s+/).filter(Boolean);
      const wrappedLines = [];
      let currentLine = '';
      for (const word of words) {
        if (wrappedLines.length >= maxLabelLines) break;
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxLabelTextWidth) {
          currentLine = candidate;
        } else {
          if (currentLine) wrappedLines.push(trimToWidth(currentLine));
          if (wrappedLines.length >= maxLabelLines) {
            currentLine = '';
            break;
          }
          currentLine = trimToWidth(word);
        }
      }
      if (currentLine && wrappedLines.length < maxLabelLines) wrappedLines.push(trimToWidth(currentLine));

      const lines = (wrappedLines.length > 0 ? wrappedLines : [trimToWidth(label)]).slice(0, maxLabelLines);

      const textWidth = Math.ceil(
        lines.reduce((max, line) => Math.max(max, ctx.measureText(String(line)).width), 0)
      );
      const lineHeight = 12;
      const padX = 6;
      const padY = 3;
      const radius = 7;
      const boxWidth = textWidth + padX * 2;
      const boxHeight = lineHeight * lines.length + padY * 2;
      const rawBoxX = x - boxWidth / 2;
      const minX = area.left + 2;
      const maxX = area.right - boxWidth - 2;
      const boxX = Math.max(minX, Math.min(maxX, rawBoxX));
      const isEdgeClamped = Math.abs(boxX - rawBoxX) > 0.5;
      const edgeOffsetY = isEdgeClamped ? (boxHeight + 6) : 0;
      const boxY = area.top + 2 + edgeOffsetY;

      const r = Math.min(radius, boxHeight / 2, boxWidth / 2);
      ctx.beginPath();
      ctx.moveTo(boxX + r, boxY);
      ctx.lineTo(boxX + boxWidth - r, boxY);
      ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + r);
      ctx.lineTo(boxX + boxWidth, boxY + boxHeight - r);
      ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - r, boxY + boxHeight);
      ctx.lineTo(boxX + r, boxY + boxHeight);
      ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - r);
      ctx.lineTo(boxX, boxY + r);
      ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
      ctx.fill();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = stroke;
      ctx.textAlign = 'center';
      const textCenterX = boxX + boxWidth / 2;
      for (let i = 0; i < lines.length; i += 1) {
        ctx.fillText(String(lines[i]), textCenterX, boxY + padY + i * lineHeight);
      }
      ctx.restore();
    }
  },
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Filler, Tooltip, Legend, Decimation, milestonePlugin);

const CHARTS_PROJECT_STORAGE_KEY = 'ppc.charts.projectId';
const CHARTS_TASK_ORDER_STORAGE_KEY = 'ppc.charts.taskOrderId';
const CHARTS_INTERVAL_STORAGE_KEY = 'ppc.charts.interval';
const CHARTS_START_STORAGE_KEY = 'ppc.charts.start';
const CHARTS_END_STORAGE_KEY = 'ppc.charts.end';
const CHARTS_RANGE_PRESET_STORAGE_KEY = 'ppc.charts.rangePreset';
const CHARTS_ALL_PROJECTS_BY_USER_SELECTION_KEY = 'ppc.charts.allProjects.byUserSeries';
// v2: invoiced moved to the defaults; a new key so earlier saved choices pick it up.
const CHARTS_BURNDOWN_LAYERS_KEY = 'ppc.charts.burndownLayers.v2';

// The burndown always shows the budget and the budget remaining. Everything else is an
// optional layer; milestones and invoiced are on by default, the spend lines off, so the
// chart stays a burndown.
const BURNDOWN_LAYER_OPTIONS = [
  { label: 'Milestones', value: 'milestones' },
  { label: 'Projected spend (actuals + forecast)', value: 'projected' },
  { label: 'Planned spend (forecast)', value: 'planned' },
  { label: 'Invoiced to date', value: 'invoiced' },
];
const DEFAULT_BURNDOWN_LAYERS = ['milestones', 'invoiced'];

const readStoredBurndownLayers = () => {
  if (typeof window === 'undefined') return DEFAULT_BURNDOWN_LAYERS;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CHARTS_BURNDOWN_LAYERS_KEY) || 'null');
    if (!Array.isArray(parsed)) return DEFAULT_BURNDOWN_LAYERS;
    const known = new Set(BURNDOWN_LAYER_OPTIONS.map((o) => o.value));
    return parsed.filter((v) => known.has(v));
  } catch {
    return DEFAULT_BURNDOWN_LAYERS;
  }
};

const safeInt = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

const isOptionalNotImplemented = (err) => {
  const msg = String(err?.message || '');
  return /\bHTTP\s*501\b/.test(msg) || /not implemented/i.test(msg);
};

const sumTimeOffHoursInRange = (hoursByDay, start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  if (!hoursByDay || typeof hoursByDay !== 'object') return 0;

  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (a.getTime() >= b.getTime()) return 0;

  let total = 0;
  for (const [rawIso, rawHours] of Object.entries(hoursByDay)) {
    const d = parseISODate(String(rawIso || '').slice(0, 10));
    if (!d) continue;
    d.setHours(0, 0, 0, 0);
    if (d.getTime() < a.getTime() || d.getTime() >= b.getTime()) continue;
    const hours = Number(rawHours);
    if (!Number.isFinite(hours) || hours <= 0) continue;
    total += hours;
  }

  return total;
};

const resolveInterval = (raw) => {
  const v = String(raw || '').toLowerCase();
  return v === 'month' || v === 'quarter' || v === 'year' ? v : 'month';
};

const readStoredAllProjectsByUserSelection = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CHARTS_ALL_PROJECTS_BY_USER_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const values = parsed.map((v) => String(v || '')).filter((v) => v.length > 0);
    return values.length > 0 ? values : null;
  } catch {
    return null;
  }
};

const intervalOptions = [
  { label: 'Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'Year', value: 'year' },
];

const clampPeriods = (periodStarts, maxPeriods = 100) => {
  if (!Array.isArray(periodStarts)) return [];
  if (periodStarts.length <= maxPeriods) return periodStarts;
  return periodStarts.slice(periodStarts.length - maxPeriods);
};

const downloadTextFile = (filename, text, mime = 'text/plain;charset=utf-8') => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const chartToCsv = (chartData) => {
  const labels = Array.isArray(chartData?.labels) ? chartData.labels : [];
  const datasets = Array.isArray(chartData?.datasets) ? chartData.datasets : [];
  const headers = ['Period', ...datasets.map((d) => String(d?.label ?? ''))];
  const rows = labels.map((label, idx) => {
    const cols = [String(label)];
    for (const ds of datasets) cols.push(String(ds?.data?.[idx] ?? 0));
    return cols;
  });

  const escape = (v) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
  };

  return [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
};

const coerceSeriesPoints = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .map((p) => ({
        date: String(p?.date ?? p?.period_start ?? p?.periodStart ?? p?.label ?? ''),
        value: Number(p?.value ?? p?.hours ?? p?.amount ?? 0),
      }))
      .filter((p) => p.date);
  }

  const labels = Array.isArray(raw?.labels) ? raw.labels : [];
  const ds0 = Array.isArray(raw?.datasets) ? raw.datasets[0] : null;
  const data = Array.isArray(ds0?.data) ? ds0.data : [];
  return labels.map((l, idx) => ({ date: String(l), value: Number(data[idx] ?? 0) }));
};

const normalizePeriodLabelToIsoDate10 = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return null;

  // Already ISO date.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // YYYY-MM -> YYYY-MM-01
  const mMonth = s.match(/^(\d{4})-(\d{2})$/);
  if (mMonth) return `${mMonth[1]}-${mMonth[2]}-01`;

  // YYYY -> YYYY-01-01
  const mYear = s.match(/^(\d{4})$/);
  if (mYear) return `${mYear[1]}-01-01`;

  // YYYY-Qn -> first month of the quarter
  const mQuarter = s.match(/^(\d{4})-Q([1-4])$/i);
  if (mQuarter) {
    const year = mQuarter[1];
    const q = Number(mQuarter[2]);
    const month = q === 1 ? '01' : q === 2 ? '04' : q === 3 ? '07' : '10';
    return `${year}-${month}-01`;
  }

  return null;
};

// Step 2: aggregateDataByInterval(rawData, interval, rangeStart, rangeEnd)
const aggregateDataByInterval = (rawData, interval, rangeStart, rangeEnd) => {
  const points = coerceSeriesPoints(rawData);
  const starts = clampPeriods(buildPeriodStarts({ interval, rangeStart, rangeEnd }), 100);

  if (!starts.length) {
    return points
      .map((p) => ({
        period_start: normalizePeriodLabelToIsoDate10(p.date) ?? String(p.date ?? ''),
        value: Number(p.value ?? 0),
      }))
      .filter((p) => p.period_start);
  }

  const sums = new Map();
  for (const p of points) {
    const normalized = normalizePeriodLabelToIsoDate10(p.date) ?? String(p.date ?? '');
    const d = parseISODate(normalized);
    if (!d) continue;
    const bucket = bucketStartForInterval(interval, d);
    const iso = toISODate(bucket);
    if (!iso) continue;
    sums.set(iso, Number(sums.get(iso) ?? 0) + Number(p.value ?? 0));
  }

  return starts.map((s) => {
    const iso = toISODate(s);
    return { period_start: iso, value: Number(sums.get(iso) ?? 0) };
  });
};

// Step 2: transformToChartJsFormat(aggregatedData, datasetLabel, chartTheme)
const transformToChartJsFormat = (aggregatedData, datasetLabel, chartTheme) => {
  const rows = Array.isArray(aggregatedData) ? aggregatedData : [];
  const labels = rows.map((r) => String(r?.period_start ?? ''));
  const data = rows.map((r) => Number(r?.value ?? 0));

  return {
    labels,
    datasets: [
      {
        label: datasetLabel,
        data,
        borderColor: chartTheme.primary,
        backgroundColor: chartTheme.primaryFill,
        fill: true,
        tension: 0.1,
        pointRadius: 2,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
    ],
  };
};

const getSeriesColorByIndex = (index, chartTheme) => {
  const idx = Number(index);
  const safeIdx = Number.isInteger(idx) && idx >= 0 ? idx : 0;
  const palette = Array.isArray(chartTheme?.userPalette) ? chartTheme.userPalette.filter(Boolean) : [];

  if (safeIdx < palette.length) return palette[safeIdx];

  const goldenAngle = 137.508;
  const hue = Math.round(((safeIdx - palette.length) * goldenAngle) % 360);
  return `hsl(${hue < 0 ? hue + 360 : hue}, 72%, 44%)`;
};

const getByUserSeriesKey = (dataset, index = 0) => {
  const uid = Number(dataset?.userId);
  if (Number.isInteger(uid)) return `id:${uid}`;
  const label = String(dataset?.label ?? '').trim();
  return label ? `label:${label}::${index}` : `idx:${index}`;
};

// Step 2: processHoursByUserData(rawData, interval, rangeStart, rangeEnd)
const processHoursByUserData = (rawData, interval, rangeStart, rangeEnd, chartTheme) => {
  const labels = clampPeriods(buildPeriodStarts({ interval, rangeStart, rangeEnd }), 100).map((d) => toISODate(d));

  const rawLabels = Array.isArray(rawData?.labels) ? rawData.labels.map(String) : [];
  const rawDatasets = Array.isArray(rawData?.datasets) ? rawData.datasets : [];

  const datasets = rawDatasets.map((ds, idx) => {
    const points = rawLabels.map((l, i) => ({ date: l, value: Number(ds?.data?.[i] ?? 0) }));
    const aggregated = aggregateDataByInterval(points, interval, rangeStart, rangeEnd);
    const byPeriod = new Map(aggregated.map((r) => [String(r.period_start), Number(r.value ?? 0)]));
    const color = getSeriesColorByIndex(idx, chartTheme) || chartTheme.primary;

    return {
      label: String(ds?.label ?? 'User'),
      userId: Number.isInteger(Number(ds?.userId)) ? Number(ds.userId) : null,
      data: labels.map((l) => Number(byPeriod.get(l) ?? 0)),
      borderColor: color,
      backgroundColor: color,
      fill: false,
      tension: 0.1,
      pointRadius: 2,
      pointHoverRadius: 5,
      borderWidth: 2,
    };
  });

  return { labels, datasets };
};

const makeYearRanges = () => {
  const y = new Date().getFullYear();
  const yearRange = (yy) => [new Date(yy, 0, 1), new Date(yy, 11, 31)];
  return [
    { label: `This year (${y})`, value: yearRange(y) },
    { label: `Last year (${y - 1})`, value: yearRange(y - 1) },
    { label: `Next year (${y + 1})`, value: yearRange(y + 1) },
  ];
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const makeThisYearRange = (baseDate = new Date()) => {
  const d = startOfDay(baseDate);
  return [new Date(d.getFullYear(), 0, 1), new Date(d.getFullYear(), 11, 31)];
};

const makeLastYearRange = (baseDate = new Date()) => {
  const d = startOfDay(baseDate);
  const y = d.getFullYear() - 1;
  return [new Date(y, 0, 1), new Date(y, 11, 31)];
};

const makeThisQuarterRange = (baseDate = new Date()) => {
  const d = startOfDay(baseDate);
  const q0 = Math.floor(d.getMonth() / 3) * 3;
  const start = new Date(d.getFullYear(), q0, 1);
  const end = new Date(d.getFullYear(), q0 + 3, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return [start, end];
};

const makeProjectDurationRange = (project) => {
  if (!project) return null;

  const startRaw = project?.startDate ?? project?.start_date ?? project?.start;
  const endRaw = project?.endDate ?? project?.end_date ?? project?.end;

  const start = startRaw ? parseISODate(String(startRaw).slice(0, 10)) : null;
  const end = endRaw ? parseISODate(String(endRaw).slice(0, 10)) : null;

  if (!start && !end) return null;

  const a = start ? startOfDay(start) : new Date(end.getFullYear(), 0, 1);
  const b = end ? startOfDay(end) : startOfDay(new Date());
  return a.getTime() <= b.getTime() ? [a, b] : [b, a];
};

const makeAllProjectsDurationRange = (projects) => {
  const list = Array.isArray(projects) ? projects : [];
  const starts = [];
  const ends = [];

  for (const project of list) {
    const startRaw = project?.startDate ?? project?.start_date ?? project?.start;
    const endRaw = project?.endDate ?? project?.end_date ?? project?.end;
    const start = startRaw ? parseISODate(String(startRaw).slice(0, 10)) : null;
    const end = endRaw ? parseISODate(String(endRaw).slice(0, 10)) : null;
    if (start) starts.push(startOfDay(start));
    if (end) ends.push(startOfDay(end));
  }

  if (starts.length === 0 && ends.length === 0) return null;

  const a = starts.length > 0
    ? starts.reduce((min, d) => (d.getTime() < min.getTime() ? d : min))
    : ends.reduce((min, d) => (d.getTime() < min.getTime() ? d : min));

  const b = ends.length > 0
    ? ends.reduce((max, d) => (d.getTime() > max.getTime() ? d : max))
    : starts.reduce((max, d) => (d.getTime() > max.getTime() ? d : max));

  return a.getTime() <= b.getTime() ? [a, b] : [b, a];
};

const isPursuitProject = (project) => {
  return Boolean(project?.isPursuit ?? project?.is_pursuit);
};

const readProjectIdFromSearchParams = (searchParams) => {
  const rawFromUrl = searchParams?.get?.('projectId');
    if (typeof rawFromUrl === 'string' && rawFromUrl.toLowerCase() === 'all') return null; // Treat 'all' as null

  const fromUrl = safeInt(rawFromUrl);
  if (fromUrl !== null) return fromUrl;

  try {
    const stored = window.localStorage.getItem(CHARTS_PROJECT_STORAGE_KEY);
    if (typeof stored === 'string' && stored.toLowerCase() === 'all') return null; // Treat stored 'all' as null
    const fromStorage = safeInt(stored);
    if (fromStorage !== null) return fromStorage;
  } catch {
    // ignore
  }

  return null;
};

// `taskOrderId=current` asks for the task order in progress today (see pickCurrentTaskOrder); it is
// resolved once the project's task orders load, so until then there is no task order id.
const isCurrentTaskOrderRequest = (searchParams) =>
  String(searchParams?.get?.('taskOrderId') ?? '').toLowerCase() === 'current';

const readTaskOrderIdFromSearchParams = (searchParams) => {
  const rawFromUrl = searchParams?.get?.('taskOrderId') ?? searchParams?.get?.('task_order_id');
  if (typeof rawFromUrl === 'string' && rawFromUrl.toLowerCase() === 'all') return null;
  if (isCurrentTaskOrderRequest(searchParams)) return null;

  const fromUrl = safeInt(rawFromUrl);
  if (Number.isInteger(fromUrl) && fromUrl > 0) return fromUrl;

  try {
    const stored = window.localStorage.getItem(CHARTS_TASK_ORDER_STORAGE_KEY);
    if (typeof stored === 'string' && stored.toLowerCase() === 'all') return null;
    const fromStorage = safeInt(stored);
    if (Number.isInteger(fromStorage) && fromStorage > 0) return fromStorage;
  } catch {
    // ignore
  }

  return null;
};

const hasExplicitAllProjectsSelection = (searchParams) => {
  const rawFromUrl = searchParams?.get?.('projectId');
  if (typeof rawFromUrl === 'string' && rawFromUrl.toLowerCase() === 'all') return true;

  try {
    const stored = window.localStorage.getItem(CHARTS_PROJECT_STORAGE_KEY);
    return typeof stored === 'string' && stored.toLowerCase() === 'all';
  } catch {
    return false;
  }
};

const readIntervalFromSearchParams = (searchParams) => {
  const fromUrl = searchParams?.get?.('interval');
  if (fromUrl) return resolveInterval(fromUrl);

  try {
    const fromStorage = window.localStorage.getItem(CHARTS_INTERVAL_STORAGE_KEY);
    return resolveInterval(fromStorage);
  } catch {
    return 'month';
  }
};

const readRangeFromSearchParams = (searchParams) => {
  const startFromUrl = searchParams?.get?.('start');
  const endFromUrl = searchParams?.get?.('end');

  const start = String(startFromUrl || '').slice(0, 10);
  const end = String(endFromUrl || '').slice(0, 10);
  const ds = parseISODate(start);
  const de = parseISODate(end);
  if (ds && de) return [ds, de];

  try {
    const storedStart = String(window.localStorage.getItem(CHARTS_START_STORAGE_KEY) || '').slice(0, 10);
    const storedEnd = String(window.localStorage.getItem(CHARTS_END_STORAGE_KEY) || '').slice(0, 10);
    const sds = parseISODate(storedStart);
    const sde = parseISODate(storedEnd);
    if (!sds || !sde) return null;
    return [sds, sde];
  } catch {
    return null;
  }
};

const normalizeRangePreset = (raw) => {
  const v = String(raw || '').toLowerCase();
  if (
    v === 'this-quarter' ||
    v === 'this-year' ||
    v === 'last-year' ||
    v === 'project-duration' ||
    v === 'custom'
  ) {
    return v;
  }
  return null;
};

const readRangePresetFromSearchParams = (searchParams) => {
  const fromUrl = normalizeRangePreset(searchParams?.get?.('rangePreset'));
  if (fromUrl) return fromUrl;

  try {
    const fromStorage = normalizeRangePreset(window.localStorage.getItem(CHARTS_RANGE_PRESET_STORAGE_KEY));
    if (fromStorage) return fromStorage;
  } catch {
    // ignore
  }

  return null;
};

const makeChartTheme = (isDark = false) => {
  const primary = readCssVar('--rs-primary-500', '#1675e0');
  // Derive text/grid colors from the active theme directly. Reading the rsuite CSS
  // vars off the DOM is unreliable here because rsuite scopes them to `body.rs-theme-*`
  // (not <html>), and this runs during render before the class may be applied.
  const text = isDark ? '#e4e6eb' : '#111';
  const textMuted = isDark ? '#a4a9b3' : '#666';
  const grid = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)';
  const tooltipBg = isDark ? '#1a1d24' : '#fff';
  const tooltipBorder = grid;
  const tooltipText = text;

  const userPalette = [
    primary,
    readCssVar('--rs-blue-500', '#0b5fff'),
    readCssVar('--rs-green-500', '#2ecc71'),
    readCssVar('--rs-orange-500', '#f39c12'),
    readCssVar('--rs-red-500', '#e74c3c'),
    readCssVar('--rs-violet-500', '#8e44ad'),
    readCssVar('--rs-cyan-500', '#00bcd4'),
  ].filter(Boolean);

  const primaryFill = (() => {
    const s = String(primary).trim();
    const hex = s.match(/^#([0-9a-f]{6})$/i);
    if (hex) {
      const n = parseInt(hex[1], 16);
      const r = (n >> 16) & 255;
      const g = (n >> 8) & 255;
      const b = n & 255;
      return `rgba(${r}, ${g}, ${b}, 0.15)`;
    }
    const rgb = s.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
    if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, 0.15)`;
    return 'rgba(22, 117, 224, 0.15)';
  })();

  return { primary, primaryFill, text, textMuted, grid, tooltipBg, tooltipBorder, tooltipText, userPalette };
};

const BUDGET_REMAINING_LABEL = 'Budget Remaining';
const BUDGET_REMAINING_PROJECTED_LABEL = 'Budget Remaining (Projected)';
const PROJECTED_SPEND_LABEL = 'Projected Spend (actuals + forecast)';
const PLANNED_SPEND_LABEL = 'Planned Spend (forecast)';
const INVOICED_AMOUNT_LABEL = 'Invoiced Amount';

// The by-user charts draw a series per person, so the tooltip lists everyone at once and the
// dataset order it defaults to (alphabetical) buries whoever is actually over. Ranking by value
// puts the busiest person on the first line. Ties fall back to name so the order is stable from
// one hover to the next, and series with no value for the period sink to the bottom.
const tooltipItemSortByValueDesc = (a, b) => {
  const av = Number(a?.parsed?.y);
  const bv = Number(b?.parsed?.y);
  const aHasValue = Number.isFinite(av);
  const bHasValue = Number.isFinite(bv);

  if (aHasValue !== bHasValue) return aHasValue ? -1 : 1;
  if (aHasValue && av !== bv) return bv - av;

  return String(a?.dataset?.label ?? '').localeCompare(String(b?.dataset?.label ?? ''));
};

const makeBaseOptions = ({
  interval,
  includeYear,
  theme,
  title,
  yAxisTitle,
  valueFormatter,
  tooltipLabelFormatter,
  milestones,
  onLegendItemClick,
  sortTooltipItemsByValueDesc = false,
}) => {
  const milestoneNamesByLabel = new Map();
  const milestoneList = Array.isArray(milestones) ? milestones : [];
  for (const milestone of milestoneList) {
    const d = parseISODate(String(milestone?.milestone_date || '').slice(0, 10));
    if (!d) continue;
    const bucket = bucketStartForInterval(interval, d);
    const bucketIso = toISODate(bucket);
    if (!bucketIso) continue;
    const names = milestoneNamesByLabel.get(bucketIso) || [];
    names.push(String(milestone?.name || 'Milestone').trim() || 'Milestone');
    milestoneNamesByLabel.set(bucketIso, names);
  }

  return ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 300 },
  interaction: { mode: 'index', intersect: false, axis: 'x' },
  scales: {
    x: {
      type: 'category',
      grid: { color: theme.grid },
      ticks: {
        display: true,
        color: theme.textMuted,
        callback: function tickCb(value) {
          const raw = this.getLabelForValue(value);
          return formatXAxisTick(raw, interval, includeYear);
        },
        maxRotation: 0,
        autoSkip: true,
      },
      title: {
        display: false,
        text: '',
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: theme.grid },
      ticks: {
        color: theme.textMuted,
        callback: (v) => valueFormatter(Number(v)),
      },
      title: {
        display: Boolean(yAxisTitle),
        text: yAxisTitle,
        color: theme.text,
      },
    },
  },
  plugins: {
    decimation: { enabled: true, algorithm: 'lttb', samples: 100 },
    legend: {
      display: true,
      position: 'top',
      onClick: (_e, legendItem, legend) => {
        if (typeof onLegendItemClick === 'function') {
          onLegendItemClick(legendItem, legend);
          return;
        }
        const datasetIndex = legendItem?.datasetIndex;
        if (!Number.isInteger(datasetIndex)) return;
        const chart = legend?.chart;
        if (!chart) return;
        chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
        chart.update();
      },
      labels: { usePointStyle: true, color: theme.text },
    },
    title: {
      display: Boolean(title),
      text: title,
      color: theme.text,
    },
    tooltip: {
      backgroundColor: theme.tooltipBg,
      titleColor: theme.tooltipText,
      bodyColor: theme.tooltipText,
      borderColor: theme.tooltipBorder,
      borderWidth: 1,
      displayColors: true,
      ...(sortTooltipItemsByValueDesc ? { itemSort: tooltipItemSortByValueDesc } : {}),
      callbacks: {
        title: (items) => {
          const raw = items?.[0]?.label;
          return formatTooltipTitle(raw, interval, includeYear);
        },
        label: (ctx) => tooltipLabelFormatter(ctx),
        afterBody: (items) => {
          const raw = String(items?.[0]?.label || '');
          const names = milestoneNamesByLabel.get(raw) || [];
          if (!Array.isArray(names) || names.length === 0) return [];
          return ['Milestones:', ...names];
        },
      },
    },
    milestoneLines: {
      milestones,
      interval,
    },
  },
  });
};

const nextPeriodStart = (start, key) => {
  const k = String(key || 'month').toLowerCase();
  const stepMonths = k === 'year' ? 12 : k === 'quarter' ? 3 : 1;
  return new Date(start.getFullYear(), start.getMonth() + stepMonths, 1);
};

const countWeekdaysInclusive = (start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  if (a.getTime() >= b.getTime()) return 0;

  let count = 0;
  const cur = new Date(a);
  while (cur.getTime() < b.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (start, endExclusive, rows) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (a.getTime() >= b.getTime()) return 0;

  const years = new Set();
  for (let y = a.getFullYear(); y <= b.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= a.getTime() && d.getTime() < b.getTime()) {
        count += 1;
      }
    }
  }
  return count;
};

export default function ChartsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // A "Charts" link from a project or task order page carries its origin in the URL, so this
  // page can offer the way back. The ids are the source's own rather than the current
  // selection, which the user is free to change once they are here.
  const backTo = useMemo(() => {
    const from = searchParams.get('from');
    const fromProjectId = safeInt(searchParams.get('fromProjectId'));
    if (!Number.isInteger(fromProjectId)) return null;

    if (from === 'task-order') {
      const fromTaskOrderId = safeInt(searchParams.get('fromTaskOrderId'));
      if (!Number.isInteger(fromTaskOrderId)) return null;
      return { label: 'Back to task order', path: `/projects/${fromProjectId}/task-orders/${fromTaskOrderId}` };
    }

    if (from === 'project') {
      return { label: 'Back to project', path: `/projects/${fromProjectId}` };
    }

    return null;
  }, [searchParams]);

  const hoursChartRef = useRef(null);
  const byUserChartRef = useRef(null);
  const burndownChartRef = useRef(null);

  const { isDark } = useTheme();
  const theme = useMemo(() => makeChartTheme(isDark), [isDark]);

  const [projectId, setProjectId] = useState(() => readProjectIdFromSearchParams(searchParams));
  const [taskOrderId, setTaskOrderId] = useState(() => readTaskOrderIdFromSearchParams(searchParams));
  // True while a `taskOrderId=current` request waits for the task orders to load. The charts hold off
  // meanwhile, so they don't load "All task orders" first and then switch.
  const [resolvingCurrentTaskOrder, setResolvingCurrentTaskOrder] = useState(() => isCurrentTaskOrderRequest(searchParams));
  const [interval, setIntervalKey] = useState(() => readIntervalFromSearchParams(searchParams));
  const [hoursChartMetric, setHoursChartMetric] = useState('hours');
  const [byUserMetric, setByUserMetric] = useState('utilization');
  const [includePursuitsInCharts, setIncludePursuitsInCharts] = useState(false);
  const [showMilestonesPrimary, setShowMilestonesPrimary] = useState(true);
  const [burndownLayers, setBurndownLayers] = useState(readStoredBurndownLayers);
  const showMilestonesBurndown = burndownLayers.includes('milestones');

  useEffect(() => {
    try {
      window.localStorage.setItem(CHARTS_BURNDOWN_LAYERS_KEY, JSON.stringify(burndownLayers));
    } catch {
      // ignore
    }
  }, [burndownLayers]);
  const [showMilestonesByUser, setShowMilestonesByUser] = useState(false);

  const needsUtilizationData = byUserMetric === 'utilization';

  const {
    data: listedProjects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();
  // Charts only cover projects the user can open.
  const projects = useMemo(() => listedProjects.filter((p) => p?.canOpen !== false), [listedProjects]);

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const {
    data: closeDates = [],
    isLoading: closeDatesLoading,
    error: closeDatesError,
  } = useCloseDates({ enabled: needsUtilizationData });

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers({ enabled: needsUtilizationData });

  const userFteById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      const raw = Number(u?.ftePercent ?? u?.fte_percent ?? 100);
      const fte = Number.isFinite(raw) ? raw : 100;
      map.set(id, fte);
    }
    return map;
  }, [users]);

  const defaultRange = useMemo(() => {
    const r = makeYearRanges();
    return r?.[0]?.value ?? [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)];
  }, []);

  const [pendingRange, setPendingRange] = useState(() => readRangeFromSearchParams(searchParams) ?? defaultRange);
  const [committedRange, setCommittedRange] = useState(pendingRange);
  const [rangePreset, setRangePreset] = useState(() => readRangePresetFromSearchParams(searchParams) ?? 'project-duration');

  const hasPersistedRange = useMemo(() => {
    return Boolean(readRangeFromSearchParams(searchParams));
  }, [searchParams]);

  const selectedProjectFromAll = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    const list = Array.isArray(projects) ? projects : [];
    return list.find((p) => Number(p?.project_id ?? p?.id) === Number(projectId)) ?? null;
  }, [projectId, projects]);

  const isSelectedProjectPursuit = useMemo(
    () => isPursuitProject(selectedProjectFromAll),
    [selectedProjectFromAll]
  );

  const effectiveIncludePursuitsInCharts = includePursuitsInCharts || isSelectedProjectPursuit;

  const chartEligibleProjects = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    if (effectiveIncludePursuitsInCharts) return list;
    return list.filter((p) => !isPursuitProject(p));
  }, [projects, effectiveIncludePursuitsInCharts]);

  const selectedProject = useMemo(() => {
    return selectedProjectFromAll;
  }, [selectedProjectFromAll]);

  const selectedProjectDurationRange = useMemo(
    () => makeProjectDurationRange(selectedProject),
    [selectedProject]
  );

  const selectedTaskOrder = useMemo(() => {
    if (!Number.isInteger(taskOrderId)) return null;
    const list = Array.isArray(taskOrdersData) ? taskOrdersData : [];
    return list.find((o) => Number(o?.id) === Number(taskOrderId)) ?? null;
  }, [taskOrderId, taskOrdersData]);

  const selectedTaskOrderDurationRange = useMemo(
    () => makeProjectDurationRange(selectedTaskOrder),
    [selectedTaskOrder]
  );

  const selectedScopedDurationRange = useMemo(
    () => (Number.isInteger(taskOrderId) ? selectedTaskOrderDurationRange : selectedProjectDurationRange),
    [taskOrderId, selectedProjectDurationRange, selectedTaskOrderDurationRange]
  );

  const allProjectsDurationRange = useMemo(
    () => makeAllProjectsDurationRange(chartEligibleProjects),
    [chartEligibleProjects]
  );

  const selectionKey = Number.isInteger(projectId)
    ? `p:${projectId}|to:${Number.isInteger(taskOrderId) ? taskOrderId : 'all'}`
    : 'all';
  const selectionKeyRef = useRef(selectionKey);
  const hasInitializedDefaultRangeRef = useRef(false);
  const allowAllProjectsRef = useRef(hasExplicitAllProjectsSelection(searchParams));

  // Keep state in sync if user navigates via URL.
  useEffect(() => {
    const nextInterval = readIntervalFromSearchParams(searchParams);
    if (nextInterval !== interval) setIntervalKey(nextInterval);

    const nextRange = readRangeFromSearchParams(searchParams);
    if (nextRange) setPendingRange(nextRange);

    const nextRangePreset = readRangePresetFromSearchParams(searchParams);
    if (nextRangePreset && nextRangePreset !== rangePreset) setRangePreset(nextRangePreset);

    const nextProjectId = readProjectIdFromSearchParams(searchParams);
    const nextTaskOrderId = readTaskOrderIdFromSearchParams(searchParams);
    allowAllProjectsRef.current = hasExplicitAllProjectsSelection(searchParams);
    if (nextProjectId !== projectId) setProjectId(nextProjectId);
    if (nextTaskOrderId !== taskOrderId) setTaskOrderId(nextTaskOrderId);
    if (isCurrentTaskOrderRequest(searchParams)) setResolvingCurrentTaskOrder(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!resolvingCurrentTaskOrder) return;
    if (Number.isInteger(projectId)) {
      if (taskOrdersLoading) return;
      const currentId = Number(pickCurrentTaskOrder(taskOrdersData)?.id);
      setTaskOrderId(Number.isInteger(currentId) ? currentId : null);
    }
    setResolvingCurrentTaskOrder(false);
  }, [projectId, resolvingCurrentTaskOrder, taskOrdersData, taskOrdersLoading]);

  const taskOrderOptions = useMemo(() => {
    const list = Array.isArray(taskOrdersData) ? taskOrdersData : [];

    const isNamed = (o) => {
      const name = typeof o?.name === 'string' ? o.name.trim() : '';
      return name.length > 0;
    };

    const explicitNamed = list.filter((o) => !o?.isDefault && isNamed(o));
    const candidates = explicitNamed.length > 0 ? explicitNamed : list;

    return candidates
      .map((o) => ({
        label: String(o?.name || `Task order ${o?.id}`),
        value: Number(o?.id),
        isDefault: Boolean(o?.isDefault),
      }))
      .filter((o) => Number.isInteger(o.value))
      .filter((o) => (explicitNamed.length > 0 ? !o.isDefault : true))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [taskOrdersData]);

  useEffect(() => {
    if (!Number.isInteger(projectId)) {
      if (taskOrderId !== null) setTaskOrderId(null);
      return;
    }

    if (!Number.isInteger(taskOrderId)) return;
    if (taskOrdersLoading) return;
    const exists = taskOrderOptions.some((o) => Number(o.value) === Number(taskOrderId));
    if (!exists) setTaskOrderId(null);
  }, [projectId, taskOrderId, taskOrderOptions, taskOrdersLoading]);

  // Once projects load, ensure we have a valid selected project.
  useEffect(() => {
    // Don't touch the selection until projects have actually loaded. While loading,
    // `projects` is [] and treating that as "no eligible projects" would null out a
    // projectId that came from the URL — which cascades into wiping the task order,
    // range, and preset back to the "all projects / this-year" defaults.
    if (projectsLoading) return;

    const projectIds = chartEligibleProjects
      .map((p) => Number(p?.project_id ?? p?.id))
      .filter((n) => Number.isInteger(n));

    if (projectIds.length === 0) {
      if (projectId !== null) setProjectId(null);
      return;
    }

    if (projectId === null) {
      if (allowAllProjectsRef.current) return;
      if (projectIds.length > 0) {
        setProjectId(projectIds[0]);
      }
      return;
    }

    if (Number.isInteger(projectId) && projectIds.includes(projectId)) return;

    const candidate = readProjectIdFromSearchParams(searchParams);
    if (Number.isInteger(candidate) && projectIds.includes(candidate)) {
      setProjectId(candidate);
      return;
    }

    setProjectId(projectIds[0]);
  }, [projectId, chartEligibleProjects, searchParams, projectsLoading]);

  // Default date range behavior:
  // - Single project: project duration (startDate..endDate)
  // - All projects: current year
  // - Respect explicit URL range on first load only
  useEffect(() => {
    const selectionChanged = selectionKeyRef.current !== selectionKey;
    const shouldApplyDefault =
      (!hasInitializedDefaultRangeRef.current && !hasPersistedRange) ||
      (hasInitializedDefaultRangeRef.current && selectionChanged);

    if (!shouldApplyDefault) {
      selectionKeyRef.current = selectionKey;
      hasInitializedDefaultRangeRef.current = true;
      return;
    }

    const nextRange =
      selectionKey === 'all'
        ? makeThisYearRange()
        : (selectedScopedDurationRange ?? makeThisYearRange());

    setPendingRange(nextRange);
    setCommittedRange(nextRange);
    setRangePreset(selectionKey === 'all' ? 'this-year' : 'project-duration');

    selectionKeyRef.current = selectionKey;
    hasInitializedDefaultRangeRef.current = true;
  }, [hasPersistedRange, selectionKey, selectedScopedDurationRange]);

  // Debounce range changes (Step 7)
  useEffect(() => {
    const t = setTimeout(() => setCommittedRange(pendingRange), 250);
    return () => clearTimeout(t);
  }, [pendingRange]);

  const rangeStart = committedRange?.[0] instanceof Date ? committedRange[0] : null;
  const rangeEnd = committedRange?.[1] instanceof Date ? committedRange[1] : null;
  const startIso = rangeStart ? toISODate(rangeStart) : null;
  const endIso = rangeEnd ? toISODate(rangeEnd) : null;

  // Push filters to URL + localStorage.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (Number.isInteger(projectId)) next.set('projectId', String(projectId));
    else next.set('projectId', 'all'); // Set 'projectId' to 'all' instead of deleting

    if (resolvingCurrentTaskOrder) {
      next.set('taskOrderId', 'current');
    } else if (Number.isInteger(projectId) && Number.isInteger(taskOrderId) && taskOrderId > 0) {
      next.set('taskOrderId', String(taskOrderId));
    } else {
      next.delete('taskOrderId');
    }

    const safeRangePreset = normalizeRangePreset(rangePreset) ?? 'project-duration';
    next.set('rangePreset', safeRangePreset);

    next.set('interval', resolveInterval(interval));

    if (startIso && endIso) {
      next.set('start', startIso);
      next.set('end', endIso);
    } else {
      next.delete('start');
      next.delete('end');
    }

    const nextStr = next.toString();
    const curStr = String(searchParams || '');
    if (nextStr !== curStr) setSearchParams(next, { replace: true });

    try {
      if (Number.isInteger(projectId)) window.localStorage.setItem(CHARTS_PROJECT_STORAGE_KEY, String(projectId));
      else window.localStorage.setItem(CHARTS_PROJECT_STORAGE_KEY, 'all');

      if (resolvingCurrentTaskOrder) {
        // Not settled yet; keep the last saved choice until it is.
      } else if (Number.isInteger(projectId) && Number.isInteger(taskOrderId) && taskOrderId > 0) {
        window.localStorage.setItem(CHARTS_TASK_ORDER_STORAGE_KEY, String(taskOrderId));
      } else {
        window.localStorage.setItem(CHARTS_TASK_ORDER_STORAGE_KEY, 'all');
      }

      window.localStorage.setItem(CHARTS_RANGE_PRESET_STORAGE_KEY, safeRangePreset);

      window.localStorage.setItem(CHARTS_INTERVAL_STORAGE_KEY, resolveInterval(interval));
      if (startIso) window.localStorage.setItem(CHARTS_START_STORAGE_KEY, startIso);
      if (endIso) window.localStorage.setItem(CHARTS_END_STORAGE_KEY, endIso);
    } catch {
      // ignore
    }
  }, [interval, projectId, taskOrderId, rangePreset, resolvingCurrentTaskOrder, startIso, endIso, searchParams, setSearchParams]);

  const includeYear = useMemo(() => {
    if (typeof window === 'undefined') return true;
    const small = window.matchMedia?.('(max-width: 640px)')?.matches;
    return !small;
  }, []);

  const rangePresetOptions = useMemo(() => {
    const hasProjectDuration = Boolean(selectedScopedDurationRange || allProjectsDurationRange);
    return [
      { label: 'This quarter', value: 'this-quarter' },
      { label: 'This year', value: 'this-year' },
      { label: 'Last year', value: 'last-year' },
      { label: 'Project duration', value: 'project-duration', disabled: !hasProjectDuration },
      { label: 'Custom range', value: 'custom' },
    ];
  }, [selectedScopedDurationRange, allProjectsDurationRange]);

  const projectOptions = useMemo(
    () =>
      (chartEligibleProjects || [])
        .map((p) => ({
          label: String(p?.project_name ?? p?.name ?? `Project ${p?.project_id ?? p?.id}`),
          value: Number(p?.project_id ?? p?.id),
        }))
        .filter((p) => Number.isInteger(p.value))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [chartEligibleProjects]
  );

  const allProjectIds = useMemo(
    () =>
      (chartEligibleProjects || [])
        .map((p) => Number(p?.project_id ?? p?.id))
        .filter((n) => Number.isInteger(n)),
    [chartEligibleProjects]
  );

  const taskOrderPickerData = useMemo(
    () => [{ label: 'All task orders', value: 'all' }, ...taskOrderOptions],
    [taskOrderOptions]
  );

  const taskOrderPickerValue = Number.isInteger(taskOrderId) ? taskOrderId : 'all';

  const chartQueryOptions = useMemo(
    () => ({
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
    }),
    []
  );

  const isAllProjects = !Number.isInteger(projectId);

  const queryEnabled = Boolean(
    (Number.isInteger(projectId) || allProjectIds.length > 0) && startIso && endIso && !resolvingCurrentTaskOrder
  );
  const chartTaskOrderId = Number.isInteger(taskOrderId) && taskOrderId > 0 ? taskOrderId : null;

  const hoursQuery = useProjectForecastedHours(projectId, interval, startIso, endIso, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const actualHoursQuery = useProjectActualHours(projectId, interval, startIso, endIso, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const revenueQuery = useProjectForecastedRevenue(projectId, interval, startIso, endIso, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  // Cumulative series on the burndown must not depend on the selected range, so pull the
  // forecast unbounded and slice it against the visible labels.
  const revenueAllTimeQuery = useProjectForecastedRevenue(projectId, interval, null, null, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  // Projected spend (actuals + forecast), the same figures as the Staffing Outlook. All-time,
  // like the forecast above, so the cumulative line does not depend on the range shown.
  const projectedSpendQuery = useProjectProjectedSpend(projectId, interval, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const actualRevenueQuery = useProjectActualRevenue(projectId, interval, startIso, endIso, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const invoicedAmountQuery = useProjectInvoicedAmount(projectId, interval, null, null, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const byUserQuery = useProjectHoursByUser(projectId, interval, startIso, endIso, {
    taskOrderId: chartTaskOrderId,
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const milestonesQuery = useProjectMilestones(projectId, {
    taskOrderId: chartTaskOrderId,
    enabled: !isAllProjects && Number.isInteger(projectId) && !resolvingCurrentTaskOrder,
    ...chartQueryOptions,
  });
  const projectDateSpanQuery = useProjectChartDateSpan(projectId, {
    taskOrderId: chartTaskOrderId,
    enabled: !isAllProjects && Number.isInteger(projectId) && !resolvingCurrentTaskOrder,
    ...chartQueryOptions,
  });

  const tasksQuery = useTasks({ projectId, taskOrderId: chartTaskOrderId }, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });

  const allChartsSummaryQuery = useAllProjectsChartsSummary(interval, startIso, endIso, {
    enabled: queryEnabled && isAllProjects,
    includePursuits: effectiveIncludePursuitsInCharts,
    ...chartQueryOptions,
  });

  const allRevenueAllTimeQuery = useAllProjectsForecastedRevenue(interval, null, null, {
    enabled: queryEnabled && isAllProjects,
    includePursuits: effectiveIncludePursuitsInCharts,
    ...chartQueryOptions,
  });

  const allMilestonesQuery = useProjectsMilestones({
    enabled: isAllProjects,
    includePursuits: effectiveIncludePursuitsInCharts,
    ...chartQueryOptions,
  });

  // The burndown target is the budget entered on the task order, not the budget allocated across
  // its disciplines. Fall back to the allocated sum when no task order budget has been entered.
  const totalBudget = useMemo(() => {
    if (isAllProjects) {
      return Number(allChartsSummaryQuery.data?.totalBudget ?? 0);
    }

    const allocatedBudget = (tasksQuery.data || []).reduce((sum, task) => {
      const budget = Number(task?.budgetTarget ?? task?.budget_target ?? 0);
      return sum + (Number.isFinite(budget) ? budget : 0);
    }, 0);

    const taskOrders = Array.isArray(taskOrdersData) ? taskOrdersData : [];
    const scopedTaskOrders = Number.isInteger(chartTaskOrderId)
      ? taskOrders.filter((o) => Number(o?.id) === chartTaskOrderId)
      : taskOrders;

    const enteredBudget = scopedTaskOrders.reduce((sum, order) => {
      const budget = Number(order?.budgetTarget ?? order?.budget_target ?? 0);
      return sum + (Number.isFinite(budget) ? budget : 0);
    }, 0);

    return enteredBudget > 0 ? enteredBudget : allocatedBudget;
  }, [isAllProjects, allChartsSummaryQuery.data?.totalBudget, chartTaskOrderId, taskOrdersData, tasksQuery.data]);

  const allIsLoading =
    allChartsSummaryQuery.isLoading ||
    allRevenueAllTimeQuery.isLoading ||
    allMilestonesQuery.isLoading;

  const allError =
    allChartsSummaryQuery.error ||
    allRevenueAllTimeQuery.error ||
    allMilestonesQuery.error ||
    null;

  const inferredProjectDurationRange = useMemo(() => {
    const s = parseISODate(String(projectDateSpanQuery.data?.startDate || '').slice(0, 10));
    const e = parseISODate(String(projectDateSpanQuery.data?.endDate || '').slice(0, 10));
    if (!s && !e) return null;
    const a = s ? startOfDay(s) : startOfDay(e);
    const b = e ? startOfDay(e) : startOfDay(s);
    return a.getTime() <= b.getTime() ? [a, b] : [b, a];
  }, [projectDateSpanQuery.data?.endDate, projectDateSpanQuery.data?.startDate]);

  useEffect(() => {
    if (selectionKey === 'all') return;
    if (rangePreset !== 'project-duration') return;
    if (selectedScopedDurationRange) return;
    if (!inferredProjectDurationRange) return;

    const curStart = pendingRange?.[0] instanceof Date ? toISODate(pendingRange[0]) : null;
    const curEnd = pendingRange?.[1] instanceof Date ? toISODate(pendingRange[1]) : null;
    const nextStart = toISODate(inferredProjectDurationRange[0]);
    const nextEnd = toISODate(inferredProjectDurationRange[1]);
    if (curStart === nextStart && curEnd === nextEnd) return;

    setPendingRange(inferredProjectDurationRange);
    setCommittedRange(inferredProjectDurationRange);
  }, [selectionKey, rangePreset, selectedScopedDurationRange, inferredProjectDurationRange, pendingRange]);

  useEffect(() => {
    if (selectionKey !== 'all') return;
    if (rangePreset !== 'project-duration') return;
    if (!allProjectsDurationRange) return;

    const curStart = pendingRange?.[0] instanceof Date ? toISODate(pendingRange[0]) : null;
    const curEnd = pendingRange?.[1] instanceof Date ? toISODate(pendingRange[1]) : null;
    const nextStart = toISODate(allProjectsDurationRange[0]);
    const nextEnd = toISODate(allProjectsDurationRange[1]);
    if (curStart === nextStart && curEnd === nextEnd) return;

    setPendingRange(allProjectsDurationRange);
    setCommittedRange(allProjectsDurationRange);
  }, [selectionKey, rangePreset, allProjectsDurationRange, pendingRange]);

  const optionalActualHoursError = actualHoursQuery.error && !isOptionalNotImplemented(actualHoursQuery.error) ? actualHoursQuery.error : null;
  const optionalActualRevenueError =
    actualRevenueQuery.error && !isOptionalNotImplemented(actualRevenueQuery.error) ? actualRevenueQuery.error : null;
  const optionalInvoicedAmountError =
    invoicedAmountQuery.error && !isOptionalNotImplemented(invoicedAmountQuery.error) ? invoicedAmountQuery.error : null;

  const isLoading =
    projectsLoading ||
    (needsUtilizationData && (closeDatesLoading || usersLoading)) ||
    (isAllProjects
      ? allIsLoading
      : hoursQuery.isLoading || actualHoursQuery.isLoading || revenueQuery.isLoading || revenueAllTimeQuery.isLoading || invoicedAmountQuery.isLoading || byUserQuery.isLoading);
  const hasError =
    projectsError ||
    (isAllProjects
      ? allError
      : hoursQuery.error || optionalActualHoursError || revenueQuery.error || revenueAllTimeQuery.error || optionalActualRevenueError || optionalInvoicedAmountError || byUserQuery.error);
  const utilError = needsUtilizationData ? closeDatesError : null;
  const fteError = needsUtilizationData ? usersError : null;

  const workableHoursForPeriod = useCallback(
    (start, endExclusive) => {
      const weekdays = countWeekdaysInclusive(start, endExclusive);
      const closeCount = countCloseDatesInRange(start, endExclusive, closeDates);
      const hours = weekdays * 8 - closeCount * 8;
      return Math.max(0, hours);
    },
    [closeDates]
  );

  const hoursData = useMemo(() => {
    const rawForecast = isAllProjects ? allChartsSummaryQuery.data?.forecastedHours : hoursQuery.data;
    const aggregatedForecast = aggregateDataByInterval(rawForecast, interval, rangeStart, rangeEnd);
    const forecastChart = transformToChartJsFormat(aggregatedForecast, 'Forecasted Hours', theme);
    const forecastHoursColor = '#1675e0';
    const forecastDataset = {
      ...(forecastChart?.datasets?.[0] || {}),
      fill: false,
      borderColor: forecastHoursColor,
      backgroundColor: forecastHoursColor,
      pointBackgroundColor: forecastHoursColor,
      pointBorderColor: forecastHoursColor,
      pointStyle: 'circle',
    };

    const rawActual = isAllProjects
      ? allChartsSummaryQuery.data?.actualHours
      : (!actualHoursQuery.error || !isOptionalNotImplemented(actualHoursQuery.error) ? actualHoursQuery.data : null);

    if (!rawActual) {
      return {
        labels: forecastChart.labels,
        datasets: [forecastDataset],
      };
    }

    const aggregatedActual = aggregateDataByInterval(rawActual, interval, rangeStart, rangeEnd);
    const actualChart = transformToChartJsFormat(aggregatedActual, 'Actual Hours', theme);
    const actualDataset = actualChart?.datasets?.[0];
    const rawActualValues = Array.isArray(actualDataset?.data) ? actualDataset.data : [];

    const firstNonZeroIdx = rawActualValues.findIndex((v) => Number(v) !== 0);
    let lastNonZeroIdx = -1;
    for (let i = rawActualValues.length - 1; i >= 0; i -= 1) {
      if (Number(rawActualValues[i]) !== 0) {
        lastNonZeroIdx = i;
        break;
      }
    }

    if (!actualDataset || firstNonZeroIdx < 0 || lastNonZeroIdx < 0) {
      return {
        labels: forecastChart.labels,
        datasets: [forecastDataset],
      };
    }

    const trimmedActualValues = rawActualValues.map((v, idx) => {
      if (idx < firstNonZeroIdx || idx > lastNonZeroIdx) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });

    const hasRenderableActual = trimmedActualValues.some((v) => Number.isFinite(Number(v)) && Number(v) !== 0);
    if (!hasRenderableActual) {
      return {
        labels: forecastChart.labels,
        datasets: [forecastDataset],
      };
    }

    return {
      labels: forecastChart.labels,
      datasets: [
        forecastDataset,
        {
          ...actualDataset,
          data: trimmedActualValues,
          borderColor: theme.success || '#4caf50',
          backgroundColor: 'rgba(76, 175, 80, 0.28)',
          fill: {
            target: 0,
            above: 'rgba(76, 175, 80, 0.28)',
            below: 'rgba(76, 175, 80, 0.28)',
          },
          pointBackgroundColor: theme.success || '#4caf50',
          spanGaps: false,
        },
      ],
    };
  }, [
    actualHoursQuery.data,
    actualHoursQuery.error,
    allChartsSummaryQuery.data?.actualHours,
    allChartsSummaryQuery.data?.forecastedHours,
    hoursQuery.data,
    interval,
    isAllProjects,
    rangeEnd,
    rangeStart,
    theme,
  ]);

  const revenueData = useMemo(() => {
    const raw = isAllProjects ? allChartsSummaryQuery.data?.forecastedRevenue : revenueQuery.data;
    const aggregated = aggregateDataByInterval(raw, interval, rangeStart, rangeEnd);
    return transformToChartJsFormat(aggregated, 'Forecasted Revenue', theme);
  }, [allChartsSummaryQuery.data?.forecastedRevenue, interval, isAllProjects, rangeStart, rangeEnd, revenueQuery.data, theme]);

  const actualRevenueData = useMemo(() => {
    const raw = isAllProjects
      ? allChartsSummaryQuery.data?.actualRevenue
      : (!actualRevenueQuery.error || !isOptionalNotImplemented(actualRevenueQuery.error) ? actualRevenueQuery.data : null);

    if (!raw) return null;

    const aggregated = aggregateDataByInterval(raw, interval, rangeStart, rangeEnd);
    return transformToChartJsFormat(aggregated, 'Actual Dollars', theme);
  }, [allChartsSummaryQuery.data?.actualRevenue, actualRevenueQuery.data, actualRevenueQuery.error, interval, isAllProjects, rangeStart, rangeEnd, theme]);

  const forecastedRevenueAllTimePoints = useMemo(() => {
    const raw = isAllProjects ? allRevenueAllTimeQuery.data : revenueAllTimeQuery.data;

    if (!raw) return [];

    const aggregated = aggregateDataByInterval(raw, interval, null, null);
    return aggregated
      .map((r) => ({
        periodStart: String(r?.period_start ?? ''),
        value: Number(r?.value ?? 0),
      }))
      .filter((p) => p.periodStart)
      .sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));
  }, [allRevenueAllTimeQuery.data, interval, isAllProjects, revenueAllTimeQuery.data]);

  const projectedSpendAllTimePoints = useMemo(() => {
    const raw = isAllProjects ? null : projectedSpendQuery.data;
    if (!raw) return [];
    return aggregateDataByInterval(raw, interval, null, null)
      .map((r) => ({ periodStart: String(r?.period_start ?? ''), value: Number(r?.value ?? 0) }))
      .filter((p) => p.periodStart)
      .sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));
  }, [interval, isAllProjects, projectedSpendQuery.data]);

  const invoicedAmountAllTimePoints = useMemo(() => {
    const raw = isAllProjects
      ? allChartsSummaryQuery.data?.invoicedAmount
      : (!invoicedAmountQuery.error || !isOptionalNotImplemented(invoicedAmountQuery.error) ? invoicedAmountQuery.data : null);

    if (!raw) return [];

    const aggregated = aggregateDataByInterval(raw, interval, null, null);
    return aggregated
      .map((r) => ({
        periodStart: String(r?.period_start ?? ''),
        value: Number(r?.value ?? 0),
      }))
      .filter((p) => p.periodStart)
      .sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));
  }, [allChartsSummaryQuery.data?.invoicedAmount, interval, invoicedAmountQuery.data, invoicedAmountQuery.error, isAllProjects]);

  const dollarsData = useMemo(() => {
    const labels = Array.isArray(revenueData?.labels) ? revenueData.labels : [];
    const forecastValues = Array.isArray(revenueData?.datasets?.[0]?.data)
      ? revenueData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];

    const forecastDataset = {
      ...(revenueData?.datasets?.[0] || {}),
      label: 'Forecasted Dollars',
      data: forecastValues,
      fill: false,
      borderColor: '#1675e0',
      backgroundColor: '#1675e0',
      pointBackgroundColor: '#1675e0',
      pointBorderColor: '#1675e0',
    };

    if (!actualRevenueData?.datasets?.[0]) {
      return { labels, datasets: [forecastDataset] };
    }

    const actualByLabel = new Map(
      (actualRevenueData.labels || []).map((label, idx) => [String(label), Number(actualRevenueData.datasets[0].data?.[idx] ?? 0)])
    );

    const rawActualValues = labels.map((label) => Number(actualByLabel.get(String(label)) ?? 0));
    const firstNonZeroIdx = rawActualValues.findIndex((v) => Number(v) !== 0);
    let lastNonZeroIdx = -1;
    for (let i = rawActualValues.length - 1; i >= 0; i -= 1) {
      if (Number(rawActualValues[i]) !== 0) {
        lastNonZeroIdx = i;
        break;
      }
    }

    if (firstNonZeroIdx < 0 || lastNonZeroIdx < 0) {
      return { labels, datasets: [forecastDataset] };
    }

    const trimmedActualValues = rawActualValues.map((v, idx) => {
      if (idx < firstNonZeroIdx || idx > lastNonZeroIdx) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });

    const actualDataset = {
      ...actualRevenueData.datasets[0],
      label: 'Actual Dollars',
      data: trimmedActualValues,
      borderColor: theme.success || '#4caf50',
      backgroundColor: 'rgba(76, 175, 80, 0.28)',
      fill: {
        target: '-1',
        above: 'rgba(76, 175, 80, 0.28)',
        below: 'rgba(76, 175, 80, 0.28)',
      },
      pointBackgroundColor: theme.success || '#4caf50',
      spanGaps: false,
    };

    return {
      labels,
      datasets: [forecastDataset, actualDataset],
    };
  }, [actualRevenueData, revenueData, theme]);

  const primaryChartData = hoursChartMetric === 'dollars' ? dollarsData : hoursData;

  const burndownData = useMemo(() => {
    const labels = Array.isArray(revenueData?.labels) ? revenueData.labels : [];
    const revenueValues = Array.isArray(revenueData?.datasets?.[0]?.data)
      ? revenueData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];

    // Cumulative revenue runs over the whole forecast, not just the selected range, so the
    // line reads the same no matter how the range is sliced. Fall back to the in-range series
    // while the all-time query is still loading.
    const cumulativeRevenue = [];
    if (forecastedRevenueAllTimePoints.length) {
      let runningRevenue = 0;
      let revenuePtr = 0;

      for (const label of labels.map(String)) {
        while (
          revenuePtr < forecastedRevenueAllTimePoints.length
          && String(forecastedRevenueAllTimePoints[revenuePtr].periodStart) <= label
        ) {
          runningRevenue += Number(forecastedRevenueAllTimePoints[revenuePtr].value ?? 0);
          revenuePtr += 1;
        }

        cumulativeRevenue.push(runningRevenue);
      }
    } else {
      let runningTotal = 0;
      for (const value of revenueValues) {
        runningTotal += value;
        cumulativeRevenue.push(runningTotal);
      }
    }

    // Cumulative projected spend (actuals for months that are over, forecast ahead). Like
    // the forecast, it counts everything before the first visible period too.
    const hasProjectedSpend = projectedSpendAllTimePoints.length > 0;
    const cumulativeProjected = [];
    {
      let running = 0;
      let ptr = 0;
      for (const label of labels.map(String)) {
        while (ptr < projectedSpendAllTimePoints.length && String(projectedSpendAllTimePoints[ptr].periodStart) <= label) {
          running += Number(projectedSpendAllTimePoints[ptr].value ?? 0);
          ptr += 1;
        }
        cumulativeProjected.push(running);
      }
    }
    // The projected remaining budget follows what has actually been spent, then the plan.
    // Until that series loads it falls back to the forecast alone.
    const projectionBasis = hasProjectedSpend ? cumulativeProjected : cumulativeRevenue;

    const invoicedByPeriod = new Map();
    for (const point of invoicedAmountAllTimePoints) {
      const period = String(point?.periodStart || '');
      if (!period) continue;
      const value = Number(point?.value ?? 0);
      invoicedByPeriod.set(period, Number(invoicedByPeriod.get(period) ?? 0) + value);
    }

    const invoicedEntries = Array.from(invoicedByPeriod.entries())
      .map(([periodStart, value]) => ({ periodStart, value: Number(value ?? 0) }))
      .sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));

    const hasInvoicedDataset = invoicedEntries.length > 0;

    let runningInvoiced = 0;
    let invoicedPtr = 0;
    const invoicedValues = [];
    const cumulativeInvoiced = [];

    for (const label of labels.map(String)) {
      let periodAmount = 0;

      while (invoicedPtr < invoicedEntries.length && String(invoicedEntries[invoicedPtr].periodStart) <= label) {
        const entryValue = Number(invoicedEntries[invoicedPtr].value ?? 0);
        runningInvoiced += entryValue;
        if (String(invoicedEntries[invoicedPtr].periodStart) === label) periodAmount += entryValue;
        invoicedPtr += 1;
      }

      invoicedValues.push(periodAmount);
      cumulativeInvoiced.push(runningInvoiced);
    }

    const hasInvoiceAtIndex = invoicedValues.map((value) => Number(value) !== 0);

    const lastInvoicedEventIdx = (() => {
      for (let i = invoicedValues.length - 1; i >= 0; i -= 1) {
        if (Number(invoicedValues[i] ?? 0) !== 0) return i;
      }
      return -1;
    })();
    const firstInvoicedIdx = (() => {
      for (let i = 0; i < cumulativeInvoiced.length; i += 1) {
        if (Number(cumulativeInvoiced[i] ?? 0) !== 0) return i;
      }
      return -1;
    })();

    const cumulativeInvoicedVisible = hasInvoicedDataset
      ? cumulativeInvoiced.map((value, idx) => {
        if (firstInvoicedIdx < 0) return null;
        if (idx < firstInvoicedIdx) return null;
        if (lastInvoicedEventIdx >= 0 && idx > lastInvoicedEventIdx) return null;
        return value;
      })
      : null;

    const solidRemainingBudget = labels.map((_, idx) => {
      if (!hasInvoicedDataset || !Array.isArray(cumulativeInvoicedVisible)) return null;
      if (idx < firstInvoicedIdx) return null;
      if (lastInvoicedEventIdx < 0 || idx > lastInvoicedEventIdx) return null;
      return Number(totalBudget) - Number(cumulativeInvoiced[idx] ?? 0);
    });

    const projectedRemainingBudget = labels.map((_, idx) => {
      if (firstInvoicedIdx < 0) {
        return Number(totalBudget) - Number(projectionBasis[idx] ?? 0);
      }

      if (lastInvoicedEventIdx < 0) {
        const baseline = Number(cumulativeInvoiced[firstInvoicedIdx] ?? 0);
        const projectedDelta = Number(projectionBasis[idx] ?? 0) - Number(projectionBasis[firstInvoicedIdx] ?? 0);
        return Number(totalBudget) - (baseline + projectedDelta);
      }
      if (idx <= lastInvoicedEventIdx) return null;

      const invoicedBaseline = Number(cumulativeInvoiced?.[lastInvoicedEventIdx] ?? 0);
      const projectedDelta = Number(projectionBasis[idx] ?? 0) - Number(projectionBasis[lastInvoicedEventIdx] ?? 0);
      return Number(totalBudget) - (invoicedBaseline + projectedDelta);
    });

    // The last invoiced month is a known number, not a projection. It is repeated on the projected
    // series only so the dashed line joins the solid one; the tooltip filters it back out.
    if (lastInvoicedEventIdx >= 0 && lastInvoicedEventIdx < labels.length) {
      projectedRemainingBudget[lastInvoicedEventIdx] = Number(totalBudget) - Number(cumulativeInvoiced?.[lastInvoicedEventIdx] ?? 0);
    }

    const budgetLine = labels.map(() => totalBudget);

    const datasets = [
        {
          label: 'Budget Target',
          data: budgetLine,
          borderColor: theme.danger || '#f44336',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          tension: 0,
        },
        {
          label: BUDGET_REMAINING_LABEL,
          data: solidRemainingBudget,
          borderColor: theme.warning || '#f0ad4e',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: BUDGET_REMAINING_PROJECTED_LABEL,
          data: projectedRemainingBudget,
          borderColor: theme.warning || '#f0ad4e',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          spanGaps: true,
          tension: 0.1,
        },
        {
          label: PROJECTED_SPEND_LABEL,
          data: hasProjectedSpend ? cumulativeProjected : labels.map(() => null),
          borderColor: theme.success || '#4caf50',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 2,
          pointBackgroundColor: theme.success || '#4caf50',
          tension: 0.1,
        },
        {
          label: PLANNED_SPEND_LABEL,
          data: cumulativeRevenue,
          borderColor: theme.primary || '#3498ff',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: theme.primary || '#3498ff',
          tension: 0.1,
        },
      ];

    if (hasInvoicedDataset && Array.isArray(cumulativeInvoicedVisible)) {
      datasets.push({
          label: INVOICED_AMOUNT_LABEL,
          data: cumulativeInvoicedVisible,
          borderColor: '#7c3aed',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: (ctx) => (hasInvoiceAtIndex[ctx.dataIndex] ? 3 : 0),
          pointHoverRadius: (ctx) => (hasInvoiceAtIndex[ctx.dataIndex] ? 5 : 0),
          pointBackgroundColor: '#7c3aed',
          spanGaps: false,
          tension: 0.1,
      });
    }

    return {
      labels,
      datasets,
      // Not drawn; read by the tooltip so the projected spend is always visible on hover.
      meta: { cumulativeProjected: hasProjectedSpend ? cumulativeProjected : null },
    };
  }, [forecastedRevenueAllTimePoints, invoicedAmountAllTimePoints, projectedSpendAllTimePoints, revenueData, totalBudget, theme]);

  // What the chart draws: the full burndown data (which export still uses in full) less the
  // optional layers that are switched off.
  const burndownVisibleData = useMemo(() => {
    const hiddenLabels = new Set();
    if (!burndownLayers.includes('projected')) hiddenLabels.add(PROJECTED_SPEND_LABEL);
    if (!burndownLayers.includes('planned')) hiddenLabels.add(PLANNED_SPEND_LABEL);
    if (!burndownLayers.includes('invoiced')) hiddenLabels.add(INVOICED_AMOUNT_LABEL);
    return {
      ...burndownData,
      datasets: (burndownData.datasets || []).filter((ds) => !hiddenLabels.has(String(ds?.label || ''))),
    };
  }, [burndownData, burndownLayers]);

  const byUserData = useMemo(() => {
    const raw = isAllProjects ? allChartsSummaryQuery.data?.hoursByUser : byUserQuery.data;
    return processHoursByUserData(raw, interval, rangeStart, rangeEnd, theme);
  }, [allChartsSummaryQuery.data?.hoursByUser, byUserQuery.data, interval, isAllProjects, rangeStart, rangeEnd, theme]);

  const byUserIdsForUtil = useMemo(() => {
    if (!needsUtilizationData) return [];
    const ids = new Set();
    for (const ds of byUserData?.datasets || []) {
      const id = Number(ds?.userId);
      if (Number.isInteger(id)) ids.add(id);
    }
    return Array.from(ids.values());
  }, [byUserData, needsUtilizationData]);

  const byUserTimeOffQueries = useUsersTimeOffDays(byUserIdsForUtil, {
    enabled: needsUtilizationData && byUserIdsForUtil.length > 0,
  });

  const byUserTimeOffError = useMemo(
    () => byUserTimeOffQueries.find((q) => q?.error)?.error || null,
    [byUserTimeOffQueries]
  );

  const byUserTimeOffHoursByDay = useMemo(() => {
    const map = new Map();

    byUserIdsForUtil.forEach((userId, idx) => {
      const q = byUserTimeOffQueries?.[idx];
      const payload = q?.data;
      const byDay = {};

      const hasByDay =
        payload &&
        typeof payload === 'object' &&
        payload.time_off_hours_by_day &&
        typeof payload.time_off_hours_by_day === 'object';

      if (hasByDay) {
        for (const [rawDay, rawHours] of Object.entries(payload.time_off_hours_by_day)) {
          const day = String(rawDay || '').slice(0, 10);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) continue;
          const hours = Number(rawHours);
          byDay[day] = Number.isFinite(hours) && hours >= 0 ? hours : 8;
        }
      }

      if (!hasByDay && Array.isArray(payload?.entries)) {
        for (const entry of payload.entries) {
          const day = String(entry?.date ?? entry?.time_off_date ?? '').slice(0, 10);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) continue;
          const hours = Number(entry?.hours);
          byDay[day] = Number.isFinite(hours) && hours >= 0 ? hours : 8;
        }
      }

      map.set(userId, byDay);
    });

    return map;
  }, [byUserIdsForUtil, byUserTimeOffQueries]);

  const byUserUtilizationData = useMemo(() => {
    const labels = Array.isArray(byUserData?.labels) ? byUserData.labels.map(String) : [];
    const labelDates = labels.map((l) => parseISODate(l)).filter(Boolean);

    const periodWorkable = new Map();
    const periodBounds = new Map();
    for (let i = 0; i < labels.length; i += 1) {
      const start = parseISODate(labels[i]);
      if (!start) continue;

      const end = (() => {
        const next = labels[i + 1] ? parseISODate(labels[i + 1]) : null;
        if (next) return next;
        return nextPeriodStart(start, interval);
      })();

      periodWorkable.set(labels[i], workableHoursForPeriod(start, end));
      periodBounds.set(labels[i], { start, end });
    }

    const datasets = (byUserData?.datasets || []).map((ds) => {
      const dsUserId = Number(ds?.userId);
      const ftePercent = Number.isInteger(dsUserId) ? userFteById.get(dsUserId) ?? 100 : 100;
      const fteScale = Math.max(0, Number(ftePercent) / 100);
      const userTimeOffByDay = Number.isInteger(dsUserId) ? byUserTimeOffHoursByDay.get(dsUserId) : null;

      const hoursArr = Array.isArray(ds?.data) ? ds.data : [];
      // Order of operations: working hours in the period, less time off, and only then scaled
      // by the non-reserved share. Reserved hours are a share of the time the user is actually
      // at work, so applying fteScale before time off is removed double-counts the reduction.
      const pctArr = labels.map((label, idx) => {
        const workingHours = Number(periodWorkable.get(label) ?? 0);
        const bounds = periodBounds.get(label);
        const timeOffHours = bounds ? sumTimeOffHoursInRange(userTimeOffByDay, bounds.start, bounds.end) : 0;
        const afterTimeOff = Math.max(0, workingHours - (Number.isFinite(timeOffHours) ? timeOffHours : 0));
        const workable = afterTimeOff * fteScale;
        const hours = Number(hoursArr[idx] ?? 0);
        if (!Number.isFinite(workable) || workable <= 0) return 0;
        if (!Number.isFinite(hours) || hours <= 0) return 0;
        return (hours / workable) * 100;
      });
      return { ...ds, data: pctArr };
    });

    return { labels, datasets, _labelDates: labelDates };
  }, [byUserData, byUserTimeOffHoursByDay, interval, workableHoursForPeriod, userFteById]);

  const chartMilestones = useMemo(() => {
    const rawMilestones = isAllProjects
      ? (Array.isArray(allMilestonesQuery.data) ? allMilestonesQuery.data : [])
      : (Array.isArray(milestonesQuery.data) ? milestonesQuery.data : []);

    const projectNameById = new Map(
      (projects || [])
        .map((p) => {
          const id = Number(p?.project_id ?? p?.id);
          const label = String(p?.project_name ?? p?.name ?? '').trim();
          return [id, label];
        })
        .filter(([id, label]) => Number.isInteger(id) && label)
    );

    return rawMilestones
      .map((milestone) => {
      const projectKey = Number(milestone?.project_id ?? milestone?.projectId);
      const projectLabel = projectNameById.get(projectKey) || null;
      const baseName = String(milestone?.name || 'Milestone').trim() || 'Milestone';
      const displayName = isAllProjects && projectLabel ? `${projectLabel}: ${baseName}` : baseName;

        return {
          ...milestone,
          name: displayName,
        };
      })
      .sort((a, b) => {
        const ad = String(a?.milestone_date || '');
        const bd = String(b?.milestone_date || '');
        if (ad !== bd) return ad.localeCompare(bd);
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      });
  }, [allMilestonesQuery.data, isAllProjects, milestonesQuery.data, projects]);

  const visiblePrimaryMilestones = useMemo(
    () => (showMilestonesPrimary ? chartMilestones : []),
    [showMilestonesPrimary, chartMilestones]
  );
  const visibleBurndownMilestones = useMemo(
    () => (showMilestonesBurndown ? chartMilestones : []),
    [showMilestonesBurndown, chartMilestones]
  );
  const visibleByUserMilestones = useMemo(
    () => (showMilestonesByUser ? chartMilestones : []),
    [showMilestonesByUser, chartMilestones]
  );

  const activeByUserData = byUserMetric === 'utilization' ? byUserUtilizationData : byUserData;
  const byUserSeriesOptions = useMemo(() => {
    const datasets = Array.isArray(activeByUserData?.datasets) ? activeByUserData.datasets : [];
    return datasets.map((ds, idx) => ({
      label: String(ds?.label ?? `User ${idx + 1}`),
      value: getByUserSeriesKey(ds, idx),
    }));
  }, [activeByUserData]);

  const byUserSeriesValues = useMemo(() => byUserSeriesOptions.map((o) => o.value), [byUserSeriesOptions]);

  const byUserSeriesLabelByValue = useMemo(() => {
    const map = new Map();
    for (const opt of byUserSeriesOptions) {
      map.set(opt.value, opt.label);
    }
    return map;
  }, [byUserSeriesOptions]);

  const [selectedByUserSeriesKeys, setSelectedByUserSeriesKeys] = useState(null);

  useEffect(() => {
    if (isAllProjects) {
      const stored = readStoredAllProjectsByUserSelection();
      setSelectedByUserSeriesKeys(stored ?? null);
      return;
    }

    setSelectedByUserSeriesKeys(null);
  }, [isAllProjects, projectId]);

  useEffect(() => {
    if (!isAllProjects || !Array.isArray(selectedByUserSeriesKeys)) return;
    try {
      window.localStorage.setItem(CHARTS_ALL_PROJECTS_BY_USER_SELECTION_KEY, JSON.stringify(selectedByUserSeriesKeys));
    } catch {
      // ignore
    }
  }, [isAllProjects, selectedByUserSeriesKeys]);

  useEffect(() => {
    const allValues = byUserSeriesValues;
    setSelectedByUserSeriesKeys((prev) => {
      if (allValues.length === 0) return prev;

      const next = (() => {
        if (prev === null) return allValues;
        const prevSet = new Set(Array.isArray(prev) ? prev : []);
        const filtered = allValues.filter((v) => prevSet.has(v));
        if (prevSet.size > 0 && filtered.length === 0) return allValues;
        return filtered;
      })();

      const prevList = Array.isArray(prev) ? prev : [];
      if (next.length === prevList.length && next.every((v, i) => v === prevList[i])) {
        return prev;
      }
      return next;
    });
  }, [byUserSeriesValues]);

  const selectedByUserSeriesSet = useMemo(() => {
    const list = Array.isArray(selectedByUserSeriesKeys)
      ? selectedByUserSeriesKeys
      : byUserSeriesValues;
    return new Set(list);
  }, [selectedByUserSeriesKeys, byUserSeriesValues]);

  const activeByUserDataWithVisibility = useMemo(() => {
    const labels = Array.isArray(activeByUserData?.labels) ? activeByUserData.labels : [];
    const datasets = Array.isArray(activeByUserData?.datasets) ? activeByUserData.datasets : [];
    const selectedSet = selectedByUserSeriesSet;

    return {
      ...(activeByUserData || {}),
      labels,
      datasets: datasets.map((ds, idx) => {
        const key = getByUserSeriesKey(ds, idx);
        return {
          ...ds,
          hidden: !selectedSet.has(key),
        };
      }),
    };
  }, [activeByUserData, selectedByUserSeriesSet]);

  const selectAllByUserSeries = useCallback(() => {
    setSelectedByUserSeriesKeys(byUserSeriesOptions.map((o) => o.value));
  }, [byUserSeriesOptions]);

  const deselectAllByUserSeries = useCallback(() => {
    setSelectedByUserSeriesKeys([]);
  }, []);

  const renderByUserSelectionValue = useCallback(
    (value) => {
      const selectedValues = Array.isArray(value) ? value : [];
      const labels = selectedValues
        .map((v) => byUserSeriesLabelByValue.get(v))
        .filter((name) => typeof name === 'string' && name.trim().length > 0);

      if (labels.length === 0) return 'No people selected';
      if (labels.length <= 3) return labels.join(', ');

      const firstThree = labels.slice(0, 3).join(', ');
      return `${firstThree} … ${labels.length - 3} more`;
    },
    [byUserSeriesLabelByValue]
  );

  const onByUserLegendItemClick = useCallback(
    (legendItem, legend) => {
      const datasetIndex = Number(legendItem?.datasetIndex);
      const chart = legend?.chart;
      const datasets = Array.isArray(chart?.data?.datasets) ? chart.data.datasets : [];
      if (!Number.isInteger(datasetIndex) || datasetIndex < 0 || datasetIndex >= datasets.length) return;

      const key = getByUserSeriesKey(datasets[datasetIndex], datasetIndex);
      const allValues = byUserSeriesOptions.map((o) => o.value);

      setSelectedByUserSeriesKeys((prev) => {
        const current = prev === null ? allValues : Array.isArray(prev) ? prev : [];
        const set = new Set(current);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        return allValues.filter((v) => set.has(v));
      });
    },
    [byUserSeriesOptions]
  );

  const hoursOptions = useMemo(
    () =>
      makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: 'Hours',
        valueFormatter: (v) => String(Math.round(Number(v) || 0)),
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'Hours'}: ${formatHours(ctx.parsed?.y)}`,
        milestones: visiblePrimaryMilestones,
      }),
    [interval, includeYear, theme, visiblePrimaryMilestones]
  );

  const dollarsOptions = useMemo(
    () =>
      makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: 'Dollars (USD)',
        valueFormatter: (v) => formatCurrency(v),
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'Dollars'}: ${formatCurrency(ctx.parsed?.y)}`,
        milestones: visiblePrimaryMilestones,
      }),
    [interval, includeYear, theme, visiblePrimaryMilestones]
  );

  const burndownOptions = useMemo(
    () => {
      const options = makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: 'Amount (USD)',
        valueFormatter: (v) => formatCurrency(v),
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'Amount'}: ${formatCurrency(ctx.parsed?.y)}`,
        milestones: visibleBurndownMilestones,
      });

      const existingFilter = options?.plugins?.legend?.labels?.filter;
      if (options?.plugins?.legend?.labels) {
        options.plugins.legend.labels.filter = (legendItem, data) => {
          if (String(legendItem?.text || '') === BUDGET_REMAINING_PROJECTED_LABEL) return false;
          if (typeof existingFilter === 'function') return existingFilter(legendItem, data);
          return true;
        };
      }

      // The projected spend line is off by default, so its figure for the hovered month is
      // always given underneath; this is the number that matches the Staffing Outlook.
      const cumulativeProjected = burndownData?.meta?.cumulativeProjected;
      if (options?.plugins?.tooltip?.callbacks && Array.isArray(cumulativeProjected) && !burndownLayers.includes('projected')) {
        options.plugins.tooltip.callbacks.footer = (items) => {
          const idx = items?.[0]?.dataIndex;
          const value = Number(cumulativeProjected[idx]);
          if (!Number.isFinite(value)) return [];
          const share = Number(totalBudget) > 0 ? ` (${((value / Number(totalBudget)) * 100).toFixed(1)}% of budget)` : '';
          return [`Projected spend through this period: ${formatCurrency(value)}${share}`];
        };
      }

      // Never report a projection for a month we already have the invoiced number for.
      if (options?.plugins?.tooltip) {
        options.plugins.tooltip.filter = (item, _index, _items, data) => {
          if (String(item?.dataset?.label || '') !== BUDGET_REMAINING_PROJECTED_LABEL) return true;
          const actual = (data?.datasets || []).find((ds) => String(ds?.label || '') === BUDGET_REMAINING_LABEL);
          const known = actual?.data?.[item?.dataIndex];
          return known === null || known === undefined;
        };
      }

      return options;
    },
    [burndownData?.meta?.cumulativeProjected, burndownLayers, interval, includeYear, theme, totalBudget, visibleBurndownMilestones]
  );

  const byUserOptions = useMemo(() => {
    if (byUserMetric === 'utilization') {
      return makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: '% Utilization',
        valueFormatter: (v) => `${Math.round(Number(v) || 0)}%`,
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'User'}: ${Math.round(Number(ctx.parsed?.y) || 0)}%`,
        milestones: visibleByUserMilestones,
        onLegendItemClick: onByUserLegendItemClick,
        sortTooltipItemsByValueDesc: true,
      });
    }

    return makeBaseOptions({
      interval,
      includeYear,
      theme,
      title: '',
      yAxisTitle: 'Hours',
      valueFormatter: (v) => String(Math.round(Number(v) || 0)),
      tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'User'}: ${formatHours(ctx.parsed?.y)}`,
      milestones: visibleByUserMilestones,
      sortTooltipItemsByValueDesc: true,
      onLegendItemClick: onByUserLegendItemClick,
    });
  }, [interval, includeYear, theme, byUserMetric, visibleByUserMilestones, onByUserLegendItemClick]);

  const totalsAndDuration = useMemo(() => {
    const hoursLabels = Array.isArray(hoursData?.labels) ? hoursData.labels.map(String) : [];
    const hoursValues = Array.isArray(hoursData?.datasets?.[0]?.data)
      ? hoursData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];

    const totalHours = hoursValues.reduce((a, b) => a + b, 0);

    const totalInvoicedAmount = invoicedAmountAllTimePoints.reduce((sum, point) => {
      return sum + Number(point?.value ?? 0);
    }, 0);
    const remainingBudget = Number(totalBudget) - Number(totalInvoicedAmount);

    const firstIdx = hoursValues.findIndex((v) => Number(v) !== 0);
    const lastIdx = (() => {
      for (let i = hoursValues.length - 1; i >= 0; i -= 1) {
        if (Number(hoursValues[i]) !== 0) return i;
      }
      return -1;
    })();

    const durationStart = firstIdx >= 0 ? hoursLabels[firstIdx] : startIso;
    const durationEnd = lastIdx >= 0 ? hoursLabels[lastIdx] : endIso;

    const durationText = durationStart && durationEnd
      ? `${formatTooltipTitle(durationStart, interval, true)} – ${formatTooltipTitle(durationEnd, interval, true)}`
      : '—';

    return { totalHours, remainingBudget, durationText };
  }, [hoursData, interval, invoicedAmountAllTimePoints, startIso, endIso, totalBudget]);

  const emptyState = useMemo(() => {
    const hasPrimary = (primaryChartData?.datasets?.[0]?.data || []).some((v) => Number(v) !== 0);
    const hasRevenue = (revenueData?.datasets?.[0]?.data || []).some((v) => Number(v) !== 0);
    const hasByUser = (activeByUserData?.datasets || []).some((ds) => (ds?.data || []).some((v) => Number(v) !== 0));
    return !(hasPrimary || hasRevenue || hasByUser);
  }, [primaryChartData, revenueData, activeByUserData]);

  const downloadMenuNode = (onPng, onCsv, onXlsx) => (
    <Dropdown
      trigger="click"
      placement="bottomEnd"
      noCaret
      size="xs"
      appearance="ghost"
      className="ppc-chart-download-menu"
      aria-label="Download"
      title={<i className="bi bi-download" aria-hidden="true" />}
    >
      <Dropdown.Item onClick={onPng}>Download PNG</Dropdown.Item>
      <Dropdown.Item onClick={onCsv}>Download CSV</Dropdown.Item>
      <Dropdown.Item onClick={() => void onXlsx?.()}>Download XLSX</Dropdown.Item>
    </Dropdown>
  );

  const burndownHeaderNode = (onPng, onCsv, onXlsx) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <div style={{ fontWeight: 600 }}>Budget Burndown</div>
      </Stack>
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <CheckPicker
          size="xs"
          searchable={false}
          cleanable={false}
          placement="bottomEnd"
          data={BURNDOWN_LAYER_OPTIONS}
          value={burndownLayers}
          onChange={(v) => setBurndownLayers(Array.isArray(v) ? v : [])}
          renderValue={(values) => `Show (${values.length})`}
          placeholder="Show"
          menuStyle={{ minWidth: 260 }}
          aria-label="Burndown layers"
        />
        {downloadMenuNode(onPng, onCsv, onXlsx)}
      </Stack>
    </Stack>
  );

  const primaryHeaderNode = (onPng, onCsv, onXlsx) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" wrap>
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <div style={{ fontWeight: 600 }}>{hoursChartMetric === 'dollars' ? 'Forecasted Dollars' : 'Forecasted Hours'}</div>
        <ButtonGroup size="xs">
          <Button
            appearance={hoursChartMetric === 'hours' ? 'primary' : 'ghost'}
            onClick={() => setHoursChartMetric('hours')}
          >
            Hours
          </Button>
          <Button
            appearance={hoursChartMetric === 'dollars' ? 'primary' : 'ghost'}
            onClick={() => setHoursChartMetric('dollars')}
          >
            Dollars
          </Button>
        </ButtonGroup>
      </Stack>

      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <Checkbox checked={showMilestonesPrimary} onChange={(_value, checked) => setShowMilestonesPrimary(Boolean(checked))}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Show milestones</span>
        </Checkbox>
        {downloadMenuNode(onPng, onCsv, onXlsx)}
      </Stack>
    </Stack>
  );

  const byUserHeaderNode = (onPng, onCsv, onXlsx) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" wrap>
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <div style={{ fontWeight: 600 }}>{byUserMetric === 'utilization' ? 'Utilization by User' : 'Hours by User'}</div>
        <ButtonGroup size="xs">
          <Button
            appearance={byUserMetric === 'hours' ? 'primary' : 'ghost'}
            onClick={() => setByUserMetric('hours')}
          >
            Hours
          </Button>
          <Button
            appearance={byUserMetric === 'utilization' ? 'primary' : 'ghost'}
            onClick={() => setByUserMetric('utilization')}
          >
            % Util
          </Button>
        </ButtonGroup>

        <CheckPicker
          data={byUserSeriesOptions}
          value={Array.isArray(selectedByUserSeriesKeys) ? selectedByUserSeriesKeys : byUserSeriesValues}
          onChange={(value) => setSelectedByUserSeriesKeys(Array.isArray(value) ? value : [])}
          renderValue={renderByUserSelectionValue}
          cleanable={false}
          searchable
          placeholder="Filter people"
          style={{ width: 220, maxWidth: 220 }}
          disabled={byUserSeriesOptions.length === 0}
          renderExtraFooter={() => (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px' }}>
              <Button appearance="link" size="xs" onClick={selectAllByUserSeries} disabled={byUserSeriesOptions.length === 0}>
                Select all
              </Button>
              <Button appearance="link" size="xs" onClick={deselectAllByUserSeries} disabled={byUserSeriesOptions.length === 0}>
                Deselect all
              </Button>
            </div>
          )}
        />
      </Stack>

      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <Checkbox checked={showMilestonesByUser} onChange={(_value, checked) => setShowMilestonesByUser(Boolean(checked))}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Show milestones</span>
        </Checkbox>
        {downloadMenuNode(onPng, onCsv, onXlsx)}
      </Stack>
    </Stack>
  );

  const exportPng = (chartRef, filename) => {
    const inst = chartRef?.current?.chart ?? chartRef?.current;
    const dataUrl = inst?.toBase64Image?.();
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportCsv = (chartData, filename) => {
    const csv = chartToCsv(chartData);
    downloadTextFile(filename, csv, 'text/csv;charset=utf-8');
  };

  const exportXlsx = async (chartData, filename, sheetName = 'Chart') => {
    const labels = Array.isArray(chartData?.labels) ? chartData.labels.map((l) => String(l ?? '')) : [];
    const datasets = Array.isArray(chartData?.datasets) ? chartData.datasets : [];

    const columns = [{ header: 'Period', key: 'period', width: 18 }];
    datasets.forEach((ds, idx) => {
      columns.push({
        header: String(ds?.label ?? `Series ${idx + 1}`),
        key: `s${idx}`,
        width: 18,
        numFmt: '0.00',
      });
    });

    const rows = labels.map((label, idx) => {
      const row = { period: label };
      datasets.forEach((ds, dsIdx) => {
        const value = Number(ds?.data?.[idx]);
        row[`s${dsIdx}`] = Number.isFinite(value) ? value : null;
      });
      return row;
    });

    await exportSheetsToXlsx({
      fileName: filename,
      sheets: [{ name: sheetName, columns, rows }],
    });
  };

  return (
    <div className="ppc-page">
      <div className="ppc-forecast-header">
        <div className="ppc-forecast-header-left">
          <h3 style={{ margin: 0, lineHeight: '22px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-bar-chart-line" aria-hidden="true" />
              Charts
            </span>
          </h3>
        </div>

        {backTo ? (
          <div className="ppc-forecast-header-right">
            <button
              type="button"
              className="ppc-charts-btn"
              onClick={() => navigate(backTo.path)}
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
              {backTo.label}
            </button>
          </div>
        ) : null}
      </div>

      <Divider />

      {hasError ? (
        <Message type="error" showIcon>
          Failed to load charts. Try adjusting filters or retry.
        </Message>
      ) : null}

      {/* The shared filter bar (nsi-components), as on EmployeeConnect's View Hours. */}
      <FilterBar label="Chart filters" maxInline={6} className="ppc-charts-filter-bar">
        <FilterField label="Project" basis={360}>
          <SelectPicker
            data={projectOptions}
            value={Number.isInteger(projectId) ? projectId : null}
            placeholder={projectsLoading ? 'Loading…' : 'All projects'}
            disabled={projectsLoading || isLoading}
            cleanable
            searchable
            menuAutoWidth={false}
            onChange={(v) => {
              if (v === null || v === undefined || v === '') {
                allowAllProjectsRef.current = true;
                setProjectId(null);
                setTaskOrderId(null);
                const r = makeThisYearRange();
                setPendingRange(r);
                setCommittedRange(r);
                setRangePreset('this-year');
                return;
              }
              const id = Number(v);
              if (!Number.isInteger(id)) return;

              allowAllProjectsRef.current = false;
              setProjectId(id);
              setTaskOrderId(null);
              const selected = (chartEligibleProjects || []).find((p) => Number(p?.project_id ?? p?.id) === id);
              const r = makeProjectDurationRange(selected) ?? allProjectsDurationRange ?? makeThisYearRange();
              setPendingRange(r);
              setCommittedRange(r);
              setRangePreset('project-duration');
            }}
          />
        </FilterField>

        {isAllProjects ? (
          <FilterField label="Scope">
            <Checkbox
              checked={effectiveIncludePursuitsInCharts}
              onChange={(_value, checked) => setIncludePursuitsInCharts(Boolean(checked))}
              disabled={isLoading}
            >
              Include pursuits
            </Checkbox>
          </FilterField>
        ) : null}

        <FilterField label="Task order" basis={280}>
          <SelectPicker
            data={taskOrderPickerData}
            value={taskOrderPickerValue}
            placeholder={Number.isInteger(projectId) ? 'All task orders' : 'Select project first'}
            disabled={!Number.isInteger(projectId) || taskOrdersLoading || isLoading}
            cleanable={false}
            searchable
            menuAutoWidth={false}
            onChange={(v) => {
              if (v === null || v === undefined || v === '' || String(v).toLowerCase() === 'all') {
                setTaskOrderId(null);
                return;
              }
              const id = Number(v);
              if (!Number.isInteger(id)) return;
              setTaskOrderId(id);
            }}
          />
        </FilterField>

        <FilterField label="Interval" basis={150}>
          <SelectPicker
            block
            data={intervalOptions}
            value={interval}
            cleanable={false}
            searchable={false}
            menuAutoWidth={false}
            menuStyle={{ width: 150, minWidth: 150 }}
            disabled={isLoading}
            onChange={(v) => setIntervalKey(resolveInterval(v))}
          />
        </FilterField>

        <FilterField label="Date range" basis={210}>
          <SelectPicker
            block
            data={rangePresetOptions}
            value={rangePreset}
            cleanable={false}
            searchable={false}
            menuAutoWidth={false}
            menuStyle={{ width: 230, minWidth: 230 }}
            disabled={isLoading}
            onChange={(next) => {
              const preset = String(next || 'custom');
              setRangePreset(preset);

              if (preset === 'custom') return;
              if (preset === 'this-quarter') {
                const r = makeThisQuarterRange();
                setPendingRange(r);
                setCommittedRange(r);
                return;
              }
              if (preset === 'this-year') {
                const r = makeThisYearRange();
                setPendingRange(r);
                setCommittedRange(r);
                return;
              }
              if (preset === 'last-year') {
                const r = makeLastYearRange();
                setPendingRange(r);
                setCommittedRange(r);
                return;
              }
              if (preset === 'project-duration') {
                const r = selectedScopedDurationRange ?? allProjectsDurationRange ?? makeThisYearRange();
                setPendingRange(r);
                setCommittedRange(r);
              }
            }}
          />
        </FilterField>

        <FilterField label="Dates" basis={300}>
          <DateRangePicker
            value={pendingRange}
            placement="bottomEnd"
            cleanable={false}
            showOneCalendar
            format="MM/dd/yyyy"
            ranges={makeYearRanges()}
            disabled={isLoading || rangePreset !== 'custom'}
            onChange={(next) => {
              if (!next || next.length !== 2) return;
              const a = next[0] instanceof Date ? next[0] : null;
              const b = next[1] instanceof Date ? next[1] : null;
              if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return;
              setPendingRange(a.getTime() <= b.getTime() ? [a, b] : [b, a]);
            }}
          />
        </FilterField>
      </FilterBar>

      <Panel bordered className="ppc-charts-top-panel">

        {isLoading ? (
          <Stack spacing={8} alignItems="center" style={{ padding: '8px 0' }}>
            <Loader content="Loading charts…" />
          </Stack>
        ) : null}

        {!isLoading && utilError ? (
          <Message type="warning" showIcon>
            Utilization chart may be incomplete (failed to load close dates).
          </Message>
        ) : null}

        {!isLoading && fteError ? (
          <Message type="warning" showIcon>
            Utilization chart is using 0% reserved hours (failed to load users).
          </Message>
        ) : null}

        {!isLoading && byUserMetric === 'utilization' && byUserTimeOffError ? (
          <Message type="warning" showIcon>
            Utilization chart may be incomplete (failed to load time off entries).
          </Message>
        ) : null}

        {!isLoading && Number.isInteger(projectId) && taskOrdersError ? (
          <Message type="warning" showIcon>
            Task order filter is unavailable (failed to load task orders).
          </Message>
        ) : null}

        {!isLoading && !hasError && emptyState ? (
          <Message type="info" showIcon>
            No forecast data for this project in the selected range. Add activities and forecasts to get started.
          </Message>
        ) : null}

        <div className="ppc-charts-grid">
          <Panel
            bordered
            className={`ppc-chart-panel${isAllProjects ? ' ppc-chart-panel--full' : ''}`}
            header={primaryHeaderNode(
              () =>
                exportPng(
                  hoursChartRef,
                  `${hoursChartMetric === 'dollars' ? 'forecasted-dollars' : 'forecasted-hours'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.png`
                ),
              () =>
                exportCsv(
                  primaryChartData,
                  `${hoursChartMetric === 'dollars' ? 'forecasted-dollars' : 'forecasted-hours'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.csv`
                ),
              () =>
                exportXlsx(
                  primaryChartData,
                  `${hoursChartMetric === 'dollars' ? 'forecasted-dollars' : 'forecasted-hours'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.xlsx`,
                  hoursChartMetric === 'dollars' ? 'Forecasted Dollars' : 'Forecasted Hours'
                )
            )}
          >
            <div className="ppc-chart-canvas" aria-label={hoursChartMetric === 'dollars' ? 'Forecasted dollars chart' : 'Forecasted hours chart'}>
              <Line ref={hoursChartRef} data={primaryChartData} options={hoursChartMetric === 'dollars' ? dollarsOptions : hoursOptions} />
            </div>
          </Panel>

          {!isAllProjects ? (
            <Panel
              bordered
              className="ppc-chart-panel"
              header={burndownHeaderNode(
                () =>
                  exportPng(
                    burndownChartRef,
                    `budget-burndown-${Number.isInteger(projectId) ? projectId : 'all'}-${interval}.png`
                  ),
                () => exportCsv(burndownData, `budget-burndown-${Number.isInteger(projectId) ? projectId : 'all'}-${interval}.csv`),
                () => exportXlsx(burndownData, `budget-burndown-${Number.isInteger(projectId) ? projectId : 'all'}-${interval}.xlsx`, 'Budget Burndown')
              )}
            >
              <div className="ppc-chart-canvas" aria-label="Budget burndown chart">
                <Line ref={burndownChartRef} data={burndownVisibleData} options={burndownOptions} />
              </div>
            </Panel>
          ) : null}

          <Panel
            bordered
            className="ppc-chart-panel ppc-chart-panel--full"
            header={byUserHeaderNode(
              () =>
                exportPng(
                  byUserChartRef,
                  `${byUserMetric === 'utilization' ? 'utilization-by-user' : 'hours-by-user'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.png`
                ),
              () =>
                exportCsv(
                  activeByUserData,
                  `${byUserMetric === 'utilization' ? 'utilization-by-user' : 'hours-by-user'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.csv`
                ),
              () =>
                exportXlsx(
                  activeByUserData,
                  `${byUserMetric === 'utilization' ? 'utilization-by-user' : 'hours-by-user'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.xlsx`,
                  byUserMetric === 'utilization' ? 'Utilization by User' : 'Hours by User'
                )
            )}
          >
            {byUserMetric === 'utilization' ? (
              <div
                style={{
                  marginBottom: 8,
                  padding: '6px 8px',
                  borderRadius: 6,
                  background: 'var(--rs-bg-well)',
                  fontSize: 12,
                  color: theme.textMuted,
                }}
              >
                The target utilization is 100%, these numbers include hours reduction and hours reserved for admin tasks
              </div>
            ) : null}
            <div className="ppc-chart-canvas" aria-label="Hours by user chart">
              <Line ref={byUserChartRef} data={activeByUserDataWithVisibility} options={byUserOptions} />
            </div>
          </Panel>

          {!isAllProjects ? (
            <Panel bordered className="ppc-chart-panel ppc-chart-panel--full" header="Metrics">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Total budget
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: '32px', color: theme.text }}>
                    {formatCurrency(totalBudget)}
                  </div>
                </Panel>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Remaining budget
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: '32px', color: theme.text }}>
                    {formatCurrency(totalsAndDuration.remainingBudget)}
                  </div>
                </Panel>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Total hours
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: '32px', color: theme.text }}>
                    {formatHours(totalsAndDuration.totalHours)}
                  </div>
                </Panel>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Project duration
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                    {totalsAndDuration.durationText}
                  </div>
                </Panel>
              </div>
            </Panel>
          ) : null}

        </div>
      </Panel>
    </div>
  );
}